import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  FlatList,
  Image,
  Keyboard,
  Platform,
  RefreshControl,
  ScrollView,
  Share,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import { useFocusEffect, useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  bossCreateUser,
  bossDeleteUser,
  createDirect,
  deleteChat,
  hideChatForMe,
  leaveGroup,
  fetchChannelsDirectory,
  fetchBossUsers,
  fetchChannelEditors,
  fetchChats,
  fetchPublicUserPreview,
  fetchSavedChat,
  fetchPublicChannelPreview,
  type PublicChannelPreview,
  markChatRead,
  setChatMuted,
  subscribeChannel,
  unsubscribeChannel,
  updateChannel,
  addChannelEditor,
  removeChannelEditor,
  searchUsersByNickname,
  buildChannelUrl,
  getUserPublicProfile,
  updateOwnProfile,
  patchNotificationSettings,
  type BossUserRow,
} from '../api/client';
import { shareTargetFromProfile, useShareContact } from '../contacts/useShareContact';
import { hapticMedium, hapticSelection } from '../util/haptics';
import { confirmAction, notifyAction } from '../util/confirmDialog';
import { buildBossInviteCopyText, loadDirectRetentionDays } from '../util/bossInviteText';
import type { ChannelDirectoryItem, ChatSummary, UserPublic } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { useLocale } from '../i18n/LocaleContext';
import { DEFAULT_LOCALE, type AppLocale, type MessageKey } from '../i18n/messages';
import type { RootStackParamList } from '../navigation/types';
import { usePaneSelection } from '../navigation/chatPane';
import { escapeWouldNavigate, useEscapeInterceptor } from '../navigation/escapeToClose';
import { useGlobalRealtime } from '../realtime/GlobalRealtimeProvider';
import { BirthdayRemindersPanel } from '../components/BirthdayRemindersPanel';
import { TintedIcon } from '../components/TintedIcon';
import { AppPressable } from '../components/AppPressable';
import { InlineRetryError } from '../components/InlineRetryError';
import { ChatListSkeleton } from '../components/Skeleton';
import { BottomSheet, type BottomSheetPalette } from '../components/BottomSheet';
import { SPACE } from '../theme/spacing';
import { FrostedBar } from '../components/FrostedBar';
import { HeaderPattern } from '../components/HeaderPattern';
import { DirectRetentionCountdown } from '../components/DirectRetentionCountdown';
import { ChatPreviewLine } from '../components/chat/ChatPreviewLine';
import { UnseenReactionBadge } from '../components/chat/UnseenReactionBadge';
import { SavedChatIcon } from '../components/SavedChatIcon';
import { AvatarBadge } from '../components/AvatarBadge';
import { ChatAvatarEditor } from '../components/ChatAvatarEditor';
import { UserAvatarPreviewModal, type UserAvatarPreviewTarget } from '../components/UserAvatarPreviewModal';
import {
  AdminShieldGlyph,
  MeetingGlyph,
  CheckGlyph,
  ComposeGlyph,
  CrossGlyph,
  MegaphoneGlyph,
  MutedBellGlyph,
  PlusGlyph,
  SearchGlyph,
  VideoCameraGlyph,
} from '../components/ChatListGlyphs';
import { SettingsModal } from './settings/SettingsModal';
import { HelpBulbButton } from '../components/HelpBulbButton';
import { MusicHeaderButton } from '../music/MusicHeaderButton';
import { HelpCenterModal, type HelpCenterPalette } from '../components/HelpCenterModal';
import { useHelpCenter } from '../help/useHelpCenter';
import { LanguagePickerRow } from '../components/LanguagePicker';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { chatListPalette } from '../theme/chatListPalette';
import { generateTemporaryPassword } from '../util/generateTemporaryPassword';
import { readChatUnreadCounts, writeChatUnreadCounts } from './chatUnreadStorage';
import { writeChatMuteStates } from './chatNotificationPrefsStorage';
import { readChatListViewPrefs, writeChatListViewPrefs, type ChatListViewFilter, type ChatListViewSort } from './chatListViewPrefsStorage';
import { subscribeOpenDruzhina } from '../notifications/openDruzhinaBridge';
import { subscribeOpenProfileByHandle } from '../deeplinks/pendingProfileBridge';
import { subscribeOpenChannelByLink } from '../deeplinks/pendingChannelBridge';
import { subscribeJoinMeetingByLink } from '../deeplinks/pendingMeetingBridge';
import { canManageMeetings } from '../meetings/meetingCreator';
import { subscribeShareToChat } from '../deeplinks/pendingShareBridge';
import type { IncomingShare } from '../share/incomingShare';
import { ShareIntentSheet } from '../components/ShareIntentSheet';
import { BellGlyph } from '../components/BellGlyph';
import { SelfStatusSheet } from '../components/SelfStatusSheet';
import { isValidPublicHandle, normalizePublicHandleInput } from '../util/publicHandle';
import { track } from '../observability/analytics';
import {
  resolveChatPreview,
  savedChat as savedChatOf,
  searchChats,
  visibleChats as visibleChatsOf,
  type ResolvedChatPreview,
} from './chat/chatListView';
import { draftPreviewLine, getAllDrafts, hydrateDrafts, subscribeDrafts } from './chat/chatDraftStorage';
import { previewForDisplay } from './chat/chatPreviewMedia';
import { chatTileCallPeerId } from './chat/chatTileActions';
import { chatRowMenuItems, isChatMutedNow, muteUntil, type ChatRowMenuItem, type MuteDuration } from './chat/chatRowMenu';
import { chatNavParamsFromSummary } from './chat/chatNavParams';
import { relationOf } from '../contacts/contactsModel';
import { refreshContacts } from '../contacts/contactsStore';
import { useContacts } from '../contacts/useContacts';
import { useProfileCard } from '../contacts/useProfileCard';
import { startCallFromChat } from '../contacts/directCall';
import { InviteSheet } from '../contacts/InviteSheet';
import { fillTemplate } from '../util/fillTemplate';
import { humanizeError } from '../util/humanizeError';
import { usePeopleSearch } from '../util/peopleSearch';
import { showToast } from '../util/toastBridge';
import {
  CHAT_DESCRIPTION_MAX_CHARS,
  CHAT_TITLE_MAX_CHARS,
  DISPLAY_NAME_MAX_CHARS,
  LOGIN_MAX_CHARS,
  PASSWORD_MAX_CHARS,
  SEARCH_QUERY_MAX_CHARS,
} from '../util/inputLimits';

/**
 * Плашка связи (UX-41) — лениво: она появляется не раньше чем через две секунды без связи, а в
 * бюджете основного веб-бандла запаса почти нет.
 */
const ConnectionBanner = React.lazy(() =>
  import('../components/ConnectionBanner').then((m) => ({ default: m.ConnectionBanner })),
);

type Props = NativeStackScreenProps<RootStackParamList, 'ChatList'>;

type BossLastCredentials = { login: string; password: string; instructionLocale: AppLocale };
type ChatFilter = ChatListViewFilter;
type ChatSort = ChatListViewSort;

/** Страница листа действий строки: сами действия, сроки заглушения, подтверждение. */
type RowMenuPage = 'main' | 'mute' | 'deleteDirect' | 'confirm';
/** Что подтверждается на странице `confirm`. */
type RowConfirm = 'leaveGroup' | 'unsubscribe' | 'deleteGroup' | 'deleteChannel';

/** Заглушение, выставленное здесь и ещё не подтверждённое следующей загрузкой списка. */
type MuteState = { muted: boolean; mutedUntil: string | null };

const SORT_OPTIONS: { sort: ChatSort; key: MessageKey }[] = [
  { sort: 'RECENT', key: 'chatSortRecent' },
  { sort: 'OLD', key: 'chatSortOld' },
  { sort: 'TITLE_ASC', key: 'chatSortTitleAsc' },
  { sort: 'TITLE_DESC', key: 'chatSortTitleDesc' },
];

const FILTERS: { filter: ChatFilter; key: MessageKey }[] = [
  { filter: 'ALL', key: 'chatListFilterAll' },
  { filter: 'DIRECT', key: 'chatListFilterPersonal' },
  { filter: 'GROUP', key: 'chatFilterGroup' },
  { filter: 'CHANNEL', key: 'chatFilterChannel' },
];

/**
 * Сообщение без вопроса (ошибка). Через notifyAction, а не `Alert.alert`: в вебе тот — пустая
 * функция, и ошибки экрана чатов там не показывались вовсе. Успех сюда больше не идёт — для
 * него тост (`showToast`), который не требует нажать OK (аудит удобства 2026-10-04).
 */
function tell(t: (key: MessageKey) => string, title: string, body?: string): void {
  void notifyAction({ title, body, okText: t('dialogOk') });
}

export function ChatListScreen({ navigation, route }: Props) {
  const insets = useSafeAreaInsets();
  const window = useWindowDimensions();
  const screen = Dimensions.get('screen');
  const bgW = Math.max(window.width, screen.width);
  const bgH = Math.max(window.height, screen.height);

  const { user, mergeUserProfile, mergeUserDoNotDisturb } = useAuth();
  const { t, locale } = useLocale();
  /** Двухпанельный режим: чат, открытый справа, — его строку подсвечиваем. Иначе null. */
  const { chatId: paneSelectedChatId, savedOpen: paneSavedOpen } = usePaneSelection();
  const {
    resolvedVisualTheme,
    chatListStyles: styles,
    chatListHeaderIconTint,
    chatListWallpaper,
    chatListWallpaperRenderer,
  } = useVisualTheme();
  const listPal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);
  /**
   * Цвета нижнего листа собираются из уже существующих токенов модалки: лист заменил её,
   * а не встал рядом, поэтому заводить ему отдельную ветку палитры не за чем. Ручка берёт
   * тон разделителя — она и есть разделитель между листом и тем, что под ним.
   */
  const sheetPalette: BottomSheetPalette = useMemo(
    () => ({
      card: listPal.modalCard,
      backdrop: listPal.modalBg,
      handle: listPal.settingsSectionDivider,
      title: listPal.modalTitle,
    }),
    [listPal],
  );
  /**
   * Цвета чипов выбора темы в листе статуса. Те же токены, что у ряда тем в окне настроек:
   * ряд один и тот же, и выглядеть в двух местах по-разному ему не с чего.
   */
  const themeChipPalette = useMemo(
    () => ({
      chipBg: listPal.modalBtn,
      chipBorder: listPal.smallBtnBorder,
      chipText: listPal.modalBtnText,
      chipSelectedBg: listPal.chatFilterChipActive,
      chipSelectedBorder: listPal.chatFilterChipActiveBorder,
      chipSelectedText: listPal.chatFilterChipTextActive,
    }),
    [listPal],
  );
  /**
   * Цвета ящика подсказок. Берутся из тех же токенов модалки, что и нижние листы: запись
   * внутри — такая же строка списка, как строка каталога каналов, поэтому и цвета у неё те же.
   */
  const helpPalette: HelpCenterPalette = useMemo(
    () => ({
      backdrop: listPal.modalBg,
      card: listPal.modalCard,
      cardBorder: listPal.settingsSectionDivider,
      title: listPal.modalTitle,
      hint: listPal.hint,
      entry: listPal.channelDirRow,
      entryBorder: listPal.channelDirRowBorder,
      entryTitle: listPal.dmRowName,
      entryMeta: listPal.channelDirMeta,
      entryBody: listPal.channelDirDescription,
      chevron: listPal.channelDirMeta,
      // Значок «Новое» — залитый акцент под белой подписью, как первичная кнопка модалки.
      newBadge: listPal.modalPrimary,
      newBadgeText: '#fff',
    }),
    [listPal],
  );
  const {
    refreshSubscribeAll,
    subscribeChatDeleted,
    subscribeChatListChanged,
    subscribeIncomingMessages,
    subscribePresence,
  } = useGlobalRealtime();
  /**
   * Контакты и заявки — общее состояние с экраном «Новое сообщение» (вариант A аудита
   * 2026-10-04). Здесь от них нужны только плашка «N заявок» и то, друг ли собеседник.
   */
  const contacts = useContacts();
  const [items, setItems] = useState<ChatSummary[]>([]);
  /** Что пришло из системного «Поделиться» и ждёт выбора чата (стори 8.7). */
  const [incomingShare, setIncomingShare] = useState<IncomingShare | null>(null);
  const [unreadByChatId, setUnreadByChatId] = useState<Record<string, number>>({});
  /**
   * Черновики по чатам: недописанное перебивает превью последнего сообщения («Черновик: …»),
   * как в Telegram, — иначе о брошенном на полуслове человек узнаёт, только открыв чат.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>(() => getAllDrafts());
  useEffect(() => {
    let alive = true;
    void hydrateDrafts().then(() => {
      if (alive) setDrafts(getAllDrafts());
    });
    const unsub = subscribeDrafts(() => setDrafts(getAllDrafts()));
    return () => {
      alive = false;
      unsub();
    };
  }, []);
  /**
   * Неотправленное по чатам (UX-48): «Не отправлено: …» перебивает и черновик, и последнее
   * сообщение — о том, что слова не дошли, человек узнаёт, не открывая чат. Хранилище очереди
   * грузится лениво: в бюджете основного веб-бандла запаса почти нет.
   */
  const outboxOwnerId = user?.userId ?? null;
  const [outboxPreview, setOutboxPreview] = useState<Record<string, string>>({});
  useEffect(() => {
    let alive = true;
    let unsub = () => {};
    void import('./chat/chatOutbox').then((outbox) => {
      if (!alive) return;
      const sync = () => {
        if (!alive) return;
        const next: Record<string, string> = {};
        for (const [chatId, list] of Object.entries(outbox.outboxByChat(outboxOwnerId))) {
          next[chatId] = outbox.outboxPreviewLine(list, locale);
        }
        setOutboxPreview(next);
      };
      void outbox.hydrateOutbox().then(sync);
      unsub = outbox.subscribeOutbox(sync);
    });
    return () => {
      alive = false;
      unsub();
    };
  }, [locale, outboxOwnerId]);
  const [presenceByChatId, setPresenceByChatId] = useState<Record<string, boolean>>({});
  const [presenceByUserId, setPresenceByUserId] = useState<Record<string, boolean>>({});
  const [unreadHydrated, setUnreadHydrated] = useState(false);
  const [loading, setLoading] = useState(true);
  /**
   * Ошибка последней загрузки списка (п. 11 аудита). Раньше сбой молча оставлял пустой
   * список, и офлайн-новичок видел «Чатов пока нет» — то есть неправду, без кнопки повторить.
   */
  const [loadError, setLoadError] = useState<unknown>(null);
  /**
   * Высота плавающей шапки. Меряется, а не считается: в неё входят безопасная зона, ряд
   * кнопок, поиск, плашка заявок и чипы фильтров. Ровно на эту величину список получает
   * верхний отступ, поэтому его первая карточка начинается сразу под шапкой, а не под ней.
   */
  const [barHeight, setBarHeight] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  /** Поиск по списку чатов (п. 1 аудита): сужает список на месте, по названию. */
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<TextInput>(null);
  const listFocused = useIsFocused();
  // Escape (десктоп, веб) очищает поиск раньше, чем закрывает чат справа: когда курсор в поле
  // поиска или когда закрывать и так нечего (navigation/escapeToClose).
  useEscapeInterceptor(
    () => {
      if (!query) return false;
      if (!searchInputRef.current?.isFocused() && escapeWouldNavigate()) return false;
      setQuery('');
      return true;
    },
    listFocused,
    'field',
  );
  const [channelsHubOpen, setChannelsHubOpen] = useState(false);
  /** Заглянуть в канал до подписки (п. 6.6): раньше по строке каталога вообще ничего не открывалось. */
  const [previewChannel, setPreviewChannel] = useState<ChannelDirectoryItem | null>(null);
  const [previewData, setPreviewData] = useState<PublicChannelPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [channelManageOpen, setChannelManageOpen] = useState(false);
  const [channelDirectory, setChannelDirectory] = useState<ChannelDirectoryItem[]>([]);
  const [channelDirectoryLoading, setChannelDirectoryLoading] = useState(false);
  const [channelHubQuery, setChannelHubQuery] = useState('');
  const [manageChannel, setManageChannel] = useState<ChannelDirectoryItem | null>(null);
  const [manageName, setManageName] = useState('');
  /**
   * Адрес канала. До этого поля его вообще нельзя было задать: сервер выводил адрес из имени,
   * а из кириллического имени не остаётся ничего годного, и канал получал 'chnl', 'chnl_2',
   * 'chnl_3'. Поле в API было с самого начала — не было формы.
   */
  const [manageSlug, setManageSlug] = useState('');
  const [manageSlugError, setManageSlugError] = useState<string | null>(null);
  const [manageDescription, setManageDescription] = useState('');
  const [manageAllowReactions, setManageAllowReactions] = useState(true);
  const [manageAllowComments, setManageAllowComments] = useState(false);
  const [manageEditors, setManageEditors] = useState<UserPublic[]>([]);
  /**
   * Поиск редакторов в управлении каналом — с паузой в наборе; ошибка запроса — строкой с
   * «Повторить», а не пустой выдачей, похожей на «никого нет» (UX-44).
   */
  const manageEditorSearch = usePeopleSearch<UserPublic>(searchUsersByNickname);
  const {
    query: manageEditorQuery,
    results: manageEditorResults,
    busy: manageEditorBusy,
    setQuery: changeManageEditorQuery,
    reset: resetManageEditorSearch,
  } = manageEditorSearch;
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Лист «Статус и оформление» — по тапу на своё имя в шапке. */
  const [selfStatusOpen, setSelfStatusOpen] = useState(false);
  /** Ссылка-приглашение из пустого списка: новичку больше некого позвать (п. P0-6). */
  const [inviteOpen, setInviteOpen] = useState(false);
  /** Порядок списка — листом с названиями, а не перебором значков «⇅ ↓» (§4 аудита). */
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  /**
   * Лист долгого нажатия на строку — один жест на все типы чатов (п. 2, 9, 10, 13). Раньше
   * удержание звонило только в личке, свайп выводил только из группы, а удаление висело
   * молнией на каждой строке.
   */
  const [menuChat, setMenuChat] = useState<ChatSummary | null>(null);
  const [menuPage, setMenuPage] = useState<RowMenuPage>('main');
  const [menuConfirm, setMenuConfirm] = useState<RowConfirm | null>(null);
  /** «Удалить и у собеседника» — галочка, по умолчанию снята: удаляем только у себя. */
  const [deleteForPeerToo, setDeleteForPeerToo] = useState(false);
  const [menuBusy, setMenuBusy] = useState(false);
  /** Заглушения, выставленные здесь, — поверх того, что пришло со списком. */
  const [muteOverrides, setMuteOverrides] = useState<Record<string, MuteState>>({});
  /**
   * «Подсказки и новости» (заказ владельца 2026-08-31).
   *
   * <p>Непрочитанные замораживаются на момент открытия: сам ящик отмечает всё прочитанным,
   * и живой список опустел бы тем же кадром — значки «Новое» пропали бы у человека на
   * глазах, не успев объяснить, ради чего мигала лампочка.
   */
  const helpCenter = useHelpCenter();
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpUnseenIds, setHelpUnseenIds] = useState<string[]>([]);
  const openHelpCenter = useCallback(() => {
    setHelpUnseenIds(helpCenter.unreadIds);
    setHelpOpen(true);
  }, [helpCenter.unreadIds]);
  /**
   * Сохранение статуса из листа.
   *
   * <p>`PATCH /api/users/me` перезаписывает профиль целиком, поэтому остальные поля идут тем
   * же значением, что лежит в профиле устройства, — ровно как это делает форма настроек
   * (`savedProfile`). Пропустить их здесь значило бы стереть человеку пол и день рождения
   * заодно со сменой статуса.
   */
  const saveSelfStatus = useCallback(
    async (statusText: string | null) => {
      if (!user) return;
      const fields = {
        login: user.login,
        displayName: user.displayName,
        bio: user.bio ?? null,
        gender: user.gender ?? null,
        birthDate: user.birthDate ?? null,
      };
      await updateOwnProfile(
        fields.login,
        fields.displayName,
        fields.bio,
        statusText,
        fields.gender,
        fields.birthDate,
      );
      await mergeUserProfile({ ...fields, statusText });
    },
    [mergeUserProfile, user],
  );
  /**
   * Вторая строка блока «кто я» в шапке: статус, без него — ник, без ника — приглашение статус
   * поставить. Пустой строки тут не бывает: место под неё уже занято, и пустота читалась бы как
   * сбой загрузки, а не как «ничего не заполнено».
   *
   * <p>При «не беспокоить» строка сообщает о тишине: рядом встаёт перечёркнутый колокольчик, а
   * текстом идёт статус, если он есть, — иначе сама подпись «Не беспокоить». Режим, о котором
   * не видно с главного экрана, живёт неделями и объясняется потом как «мне перестали писать».
   */
  const selfStatusText = user?.statusText;
  const selfDoNotDisturb = user?.doNotDisturb;
  const selfHandle = user?.publicHandle;
  const selfId = user?.userId;
  const selfStatusLine = useMemo(() => {
    const status = (selfStatusText ?? '').trim();
    if (status) return status;
    if (selfDoNotDisturb) return t('dndLabel');
    if (selfHandle) return `#${selfHandle}`;
    return t('selfStatusEmpty');
  }, [selfDoNotDisturb, selfHandle, selfStatusText, t]);
  /**
   * Переключение «не беспокоить». Ошибку не глотаем: лист показывает её сам и возвращает
   * тумблер, а тихо оставленное старое состояние выглядело бы как сработавшее нажатие.
   */
  const toggleDoNotDisturb = useCallback(
    async (next: boolean) => {
      const saved = await patchNotificationSettings(next);
      await mergeUserDoNotDisturb(saved);
    },
    [mergeUserDoNotDisturb],
  );
  /** Контакт, которым делимся: пока выбран, открыт пикер чата (стори 5.11). */
  const {
    target: shareContactTarget,
    setTarget: setShareContactTarget,
    sendToChat: sendContactCardToChat,
  } = useShareContact();
  const [bossOpen, setBossOpen] = useState(false);
  const [bossUsers, setBossUsers] = useState<BossUserRow[]>([]);
  const [bossBusy, setBossBusy] = useState(false);
  const [bossNewLogin, setBossNewLogin] = useState('');
  const [bossNewName, setBossNewName] = useState('');
  const [bossNewPassword, setBossNewPassword] = useState('');
  const [bossInstructionLocale, setBossInstructionLocale] = useState<AppLocale>(DEFAULT_LOCALE);
  const [bossLastCredentials, setBossLastCredentials] = useState<BossLastCredentials | null>(null);
  /**
   * Срок очистки личных чатов для текста приглашения — с сервера (PRIV-2). Узнаём заранее, как
   * только появились данные для копирования: в браузере запись в буфер обмена должна идти прямо
   * в обработчике нажатия, без ожидания сети.
   */
  const [bossRetentionDays, setBossRetentionDays] = useState<number | null>(null);
  useEffect(() => {
    if (!bossLastCredentials) return;
    let alive = true;
    void loadDirectRetentionDays().then((days) => {
      if (alive) setBossRetentionDays(days);
    });
    return () => {
      alive = false;
    };
  }, [bossLastCredentials]);
  const [bossError, setBossError] = useState<string | null>(null);
  const [userToDelete, setUserToDelete] = useState<BossUserRow | null>(null);
  /**
   * Фильтр не запоминается между запусками (§4 аудита): после перезапуска с оставшимся
   * фильтром «Личные» человек думал, что группы пропали. Порядок — запоминается: это выбор,
   * а не временное сужение.
   */
  const [chatFilter, setChatFilter] = useState<ChatFilter>('ALL');
  const [chatSort, setChatSort] = useState<ChatSort>('RECENT');
  const [chatListViewPrefsHydrated, setChatListViewPrefsHydrated] = useState(false);
  /**
   * Карточка пользователя из списка BOSS живёт отдельным состоянием и рисуется
   * слоем ВНУТРИ модалки BOSS. Общая карточка экрана — системный <Modal>, а
   * открывать его поверх уже открытой модалки нельзя: на iOS вторая презентация
   * подвешивает экран целиком («зашёл в управление пользователями — виснет»),
   * на Android окно уезжает под первое.
   */
  const [bossUserCard, setBossUserCard] = useState<UserAvatarPreviewTarget | null>(null);

  const resolveDirectPresence = useCallback(
    (chat: ChatSummary): boolean => {
      const raw = chat as ChatSummary & {
        peerUserId?: unknown;
        otherUserId?: unknown;
        partnerUserId?: unknown;
        userId?: unknown;
      };
      const peerUserIdRaw =
        raw.peerUserId ?? raw.otherUserId ?? raw.partnerUserId ?? raw.userId;
      const peerUserId = peerUserIdRaw != null ? String(peerUserIdRaw) : null;
      return Boolean(
        presenceByChatId[chat.id] ??
          (peerUserId ? presenceByUserId[peerUserId] : undefined) ??
          chat.peerOnline ??
          chat.isOnline ??
          chat.online,
      );
    },
    [presenceByChatId, presenceByUserId],
  );

  const resolveGroupOnlineCount = useCallback((chat: ChatSummary): number | null => {
    const rawChat = chat as ChatSummary & {
      onlineCount?: unknown;
      onlineMembersCount?: unknown;
      membersOnlineCount?: unknown;
    };
    const candidates = [rawChat.onlineCount, rawChat.onlineMembersCount, rawChat.membersOnlineCount];
    for (const value of candidates) {
      const n = typeof value === 'number' ? value : Number(value);
      if (Number.isFinite(n) && n >= 0) return Math.floor(n);
    }
    return null;
  }, []);

  /**
   * Один отложенный «мягкий» перезапрос списка на пачку событий: и присутствия (бейдж «N в
   * сети»), и новых сообщений (превью). Событий при переподключении прилетает много, дёргать
   * сеть на каждое незачем.
   */
  const softReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadInFlightRef = useRef<Promise<void> | null>(null);
  const lastLoadAtRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);

  const load = useCallback(async (mode: 'initial' | 'soft' | 'pull' = 'soft') => {
    if (loadInFlightRef.current) {
      return loadInFlightRef.current;
    }
    const now = Date.now();
    if (mode === 'soft' && hasLoadedOnceRef.current && now - lastLoadAtRef.current < 1500) {
      return;
    }
    if (mode === 'pull') {
      setRefreshing(true);
    } else if (mode === 'initial' || !hasLoadedOnceRef.current) {
      setLoading(true);
    }
    const run = (async () => {
      try {
        const rows = await fetchChats();
        setItems(rows);
        setLoadError(null);
        // Сервер подтвердил своё: локальные поправки заглушения больше не нужны.
        setMuteOverrides({});
        hasLoadedOnceRef.current = true;
        lastLoadAtRef.current = Date.now();
      } catch (e) {
        // Прежний список остаётся на месте; ошибку показываем, только если показывать нечего.
        // На 401 слой авторизации сам выводит из аккаунта.
        setLoadError(e);
      } finally {
        setLoading(false);
        setRefreshing(false);
        loadInFlightRef.current = null;
      }
    })();
    loadInFlightRef.current = run;
    return run;
  }, []);

  const loadBossUsers = useCallback(async () => {
    setBossBusy(true);
    setBossError(null);
    try {
      const rows = await fetchBossUsers();
      setBossUsers(rows);
    } catch (e) {
      setBossUsers([]);
      setBossError(humanizeError(e, t('bossUsersLoadFailed')));
    } finally {
      setBossBusy(false);
    }
  }, [t]);

  useFocusEffect(
    useCallback(() => {
      refreshSubscribeAll();
      void load(hasLoadedOnceRef.current ? 'soft' : 'initial');
      // Ошибку контактов здесь не показываем вовсе: плашке заявок она не мешает, а окно на
      // каждом возврате на экран раздражало (п. 12). Её видно на экране «Новое сообщение».
      void refreshContacts();
    }, [load, refreshSubscribeAll]),
  );

  useEffect(() => {
    return subscribeChatDeleted((deletedChatId) => {
      setItems((prev) => prev.filter((x) => x.id !== deletedChatId));
      setPresenceByChatId((prev) => {
        if (!(deletedChatId in prev)) return prev;
        const next = { ...prev };
        delete next[deletedChatId];
        return next;
      });
    });
  }, [subscribeChatDeleted]);

  useEffect(() => {
    return subscribeChatListChanged(() => {
      void load('soft');
    });
  }, [load, subscribeChatListChanged]);

  /**
   * Пуш о заявке в контакты ведёт к заявкам на экране «Новое сообщение». Мост буферизует
   * запрос, пока этот экран не смонтирован (холодный старт из уведомления).
   */
  useEffect(() => {
    return subscribeOpenDruzhina(() => {
      navigation.navigate('NewMessage', { focus: 'requests' });
    });
  }, [navigation]);

  /**
   * Переход по ссылке на публичный профиль (стори 18.3): `/u/<ник>`, `svyaz://u/<ник>` и
   * `?u=<ник>` ведут сюда, и заканчиваться это должно диалогом с человеком, а не списком чатов —
   * иначе ссылка приводит гостя в приложение и там его теряет.
   *
   * <p>Ищем в два захода. Сначала публичное превью — тем же эндпоинтом, что и сама страница.
   * Если профиль от анонимов закрыт, превью молчит, но вошедшему человека может показать
   * обычный поиск по нику: «Публичная страница профиля» и «Находить по нику в поиске» — разные
   * настройки, и вторая ссылке не мешает.
   */
  const openProfileByHandle = useCallback(
    async (handle: string) => {
      try {
        let peerId: string | null = null;
        let peer: UserPublic | null = null;
        const preview = await fetchPublicUserPreview(handle);
        if (preview) {
          peerId = preview.id;
        } else {
          const found = await searchUsersByNickname(`#${handle}`);
          const exact = found.find((candidate) => (candidate.publicHandle ?? '').toLowerCase() === handle);
          if (exact) {
            peerId = exact.id;
            peer = exact;
          }
        }
        if (!peerId) {
          tell(t, t('profileLinkUnavailable'));
          return;
        }
        if (selfId && peerId === selfId) {
          // Свою же ссылку человек открывает чаще, чем кажется: проверяет, что она работает.
          tell(t, t('profileLinkSelf'));
          return;
        }
        const chat = await createDirect(peerId);
        const enriched: ChatSummary = peer
          ? {
              ...chat,
              peerUserId: chat.peerUserId ?? peer.id,
              peerHasAvatar: chat.peerHasAvatar ?? peer.hasAvatar,
              peerAvatarRevision: chat.peerAvatarRevision ?? peer.avatarRevision,
              peerAvatarShape: chat.peerAvatarShape ?? peer.avatarShape,
            }
          : chat;
        navigation.navigate('Chat', chatNavParamsFromSummary(enriched));
        await load();
      } catch (e) {
        tell(t, t('errorTitle'), humanizeError(e, t('openChatFailed')));
      }
    },
    [load, navigation, selfId, t],
  );

  useEffect(() => {
    return subscribeOpenProfileByHandle((handle) => {
      void openProfileByHandle(handle);
    });
  }, [openProfileByHandle]);

  /**
   * Каталог каналов. Функции хаба и превью — `useCallback` от сеттеров и `t`: на них завязаны
   * открытие по ссылке и дебаунс поиска по хабу, и пересоздание на каждый рендер сбрасывало бы
   * таймер дебаунса постоянно.
   */
  const loadChannelsDirectory = useCallback(
    async (q?: string) => {
      setChannelDirectoryLoading(true);
      try {
        const rows = await fetchChannelsDirectory(q);
        setChannelDirectory(rows);
      } catch (e) {
        tell(t, t('errorTitle'), humanizeError(e, t('channelDirectoryLoadFailed')));
      } finally {
        setChannelDirectoryLoading(false);
      }
    },
    [t],
  );

  /**
   * Превью канала до подписки. Источник — публичный эндпоинт, тот же, которым живут веб-ссылки;
   * он работает по слагу, поэтому у канала без слага честно говорим, что предпросмотра нет.
   * Хаб закрываем до открытия превью: два native Modal подряд на Android часто глотают второй.
   */
  const openChannelPreview = useCallback(
    async (channel: ChannelDirectoryItem) => {
      setChannelsHubOpen(false);
      setPreviewChannel(channel);
      setPreviewData(null);
      setPreviewError(null);
      if (!channel.slug) {
        setPreviewError(t('channelPreviewUnavailable'));
        return;
      }
      setPreviewLoading(true);
      try {
        setPreviewData(await fetchPublicChannelPreview(channel.slug));
      } catch (e) {
        setPreviewError(humanizeError(e, t('channelPreviewUnavailable')));
      } finally {
        setPreviewLoading(false);
      }
    },
    [t],
  );

  /**
   * Ссылка на канал или статью (волна 2).
   *
   * <p>Раньше такие ссылки не открывал никто: кнопка «Поделиться» раздавала адреса
   * `/c/<адрес>`, а приложение знало только `/u/`. Здесь ссылка превращается либо в статью,
   * либо в предпросмотр канала — и оба варианта работают у того, кто на канал ещё не подписан.
   */
  const openChannelByLink = useCallback(
    async (target: { handle: string; postSlug: string | null }) => {
      try {
        const preview = await fetchPublicChannelPreview(target.handle);
        const mine = items.find((c) => c.id === preview.id);
        if (target.postSlug) {
          navigation.navigate('ChannelPost', {
            // chatId есть только у подписчика — по нему экран откроет полноценную статью с
            // комментариями; у гостя останется публичный путь.
            chatId: mine?.id,
            channelTitle: preview.title,
            canManagePost: mine?.myRole === 'ADMIN' || mine?.myRole === 'EDITOR',
            publicHandle: preview.handle,
            publicSlug: target.postSlug,
          });
          return;
        }
        if (mine) {
          navigation.navigate('Chat', chatNavParamsFromSummary(mine));
          return;
        }
        await loadChannelsDirectory();
        const found = (await fetchChannelsDirectory(target.handle)).find((c) => c.id === preview.id);
        if (found) {
          void openChannelPreview(found);
          return;
        }
        tell(t, t('channelPreviewUnavailable'));
      } catch (e) {
        tell(t, t('errorTitle'), humanizeError(e, t('channelPreviewUnavailable')));
      }
    },
    [items, loadChannelsDirectory, navigation, openChannelPreview, t],
  );

  useEffect(() => {
    return subscribeOpenChannelByLink((target) => {
      void openChannelByLink(target);
    });
  }, [openChannelByLink]);

  // Встреча по ссылке: ждёт здесь же, где канал и профиль, — после входа в аккаунт.
  useEffect(() => {
    return subscribeJoinMeetingByLink((token) => {
      // Вход во встречу грузится по требованию: основной бандл упирается в бюджет сборки, а по
      // ссылке на встречу приходят редко.
      void import('../meetings/joinMeeting').then(({ joinMeetingFromLink }) => joinMeetingFromLink(token));
    });
  }, []);

  /**
   * Содержимое из системного меню «Поделиться» (стори 8.7). Экран чатов — первое место, где
   * есть и список адресатов, и право спрашивать: до входа выбирать не из чего, а буфер
   * содержимое не теряет и отдаёт его, как только сюда доберутся.
   */
  useEffect(() => {
    return subscribeShareToChat((share) => {
      setIncomingShare(share);
    });
  }, []);

  useEffect(() => {
    return subscribePresence((event) => {
      if (event.chatId) {
        setPresenceByChatId((prev) => ({ ...prev, [event.chatId!]: event.online }));
      }
      if (event.userId) {
        setPresenceByUserId((prev) => ({ ...prev, [event.userId!]: event.online }));
      }
      // Бейдж «N в сети» у групп считает сервер, а состава группы у списка нет — поэтому
      // на событие присутствия перезапрашиваем список. С задержкой и коалесингом: при
      // массовом переподключении событий прилетает пачка, дёргать сеть на каждое незачем (п. 6.7).
      if (softReloadTimerRef.current) return;
      softReloadTimerRef.current = setTimeout(() => {
        softReloadTimerRef.current = null;
        void load('soft');
      }, 3000);
    });
  }, [load, subscribePresence]);

  useEffect(
    () => () => {
      if (softReloadTimerRef.current) {
        clearTimeout(softReloadTimerRef.current);
        softReloadTimerRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    return subscribeIncomingMessages((chatId) => {
      setUnreadByChatId((prev) => ({ ...prev, [chatId]: (prev[chatId] ?? 0) + 1 }));
      // Счётчик растёт сразу, а подпись под чатом — это поле списка с сервера, и без
      // перезапроса она стояла на прошлом сообщении (или «No messages yet» у только что
      // появившегося чата), пока человек не уйдёт с экрана и не вернётся (e2e 2026-09-27).
      // Тот же перезапрос возвращает в список чат, спрятанный «удалить у меня»: сервер
      // показывает его снова с первым новым сообщением.
      if (softReloadTimerRef.current) return;
      // `load('soft')` молча отказывается, если запрос уже в полёте (он мог уйти до того, как
      // сообщение легло в базу) или прошлый был меньше 1.5 с назад. Для второго сообщения
      // подряд это и было бы правилом, а не исключением, — поэтому отказ переносит попытку.
      const fire = () => {
        softReloadTimerRef.current = null;
        const since = Date.now() - lastLoadAtRef.current;
        if (loadInFlightRef.current || since < 1500) {
          softReloadTimerRef.current = setTimeout(fire, Math.max(300, 1550 - since));
          return;
        }
        void load('soft');
      };
      softReloadTimerRef.current = setTimeout(fire, 800);
    });
  }, [load, subscribeIncomingMessages]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const saved = await readChatUnreadCounts();
      if (!cancelled) {
        setUnreadByChatId(saved);
        setUnreadHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!unreadHydrated) return;
    void writeChatUnreadCounts(unreadByChatId);
  }, [unreadByChatId, unreadHydrated]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const saved = await readChatListViewPrefs();
      if (!cancelled) {
        setChatSort(saved.sort);
        setChatListViewPrefsHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!chatListViewPrefsHydrated) return;
    // Фильтр сохраняется всегда как «Все»: запоминаем только порядок (§4 аудита).
    void writeChatListViewPrefs({ filter: 'ALL', sort: chatSort });
  }, [chatSort, chatListViewPrefsHydrated]);

  /** Заглушение строки — с поправкой на ещё не подтверждённое сервером. */
  const muteOf = useCallback(
    (chat: ChatSummary): MuteState => {
      const local = muteOverrides[chat.id];
      return local ?? { muted: chat.muted === true, mutedUntil: chat.mutedUntil ?? null };
    },
    [muteOverrides],
  );
  const isMuted = useCallback((chat: ChatSummary) => isChatMutedNow(muteOf(chat)), [muteOf]);

  /**
   * Отражение заглушений на устройстве (`chatNotificationPrefsStorage`): по нему решаются звук
   * входящего по сокету и уведомление, нарисованное самим приложением. Источник — список с
   * сервера, поэтому настройка одинакова на всех устройствах человека.
   */
  useEffect(() => {
    if (!hasLoadedOnceRef.current) return;
    const states: Record<string, Date | null> = {};
    for (const chat of items) {
      const state = muteOverrides[chat.id] ?? { muted: chat.muted === true, mutedUntil: chat.mutedUntil ?? null };
      if (!isChatMutedNow(state)) continue;
      states[chat.id] = state.mutedUntil ? new Date(state.mutedUntil) : null;
    }
    void writeChatMuteStates(states);
  }, [items, muteOverrides]);

  /**
   * Переход по строке «Каталог каналов» с экрана «Новое сообщение»: тот возвращается сюда с
   * отметкой времени, и каталог открывается поверх списка.
   */
  const openHubAt = route.params?.openChannelsHub;
  const handledHubAtRef = useRef<number | undefined>(undefined);

  const openChat = useCallback(
    (chat: ChatSummary) => {
      // Щелчок раньше навигации: между касанием и первым кадром нового экрана есть зазор,
      // и без отклика он читается как «нажатие не засчиталось».
      hapticSelection();
      setUnreadByChatId((prev) => {
        if (!prev[chat.id]) return prev;
        const next = { ...prev };
        delete next[chat.id];
        return next;
      });
      navigation.navigate('Chat', chatNavParamsFromSummary(chat));
    },
    [navigation],
  );

  /**
   * Фильтр — единственное действие шапки, которое никуда не ведёт: список перестраивается
   * на месте, а повторное нажатие на активный чип возвращает к «Все». Отклика от навигации
   * здесь нет, поэтому подтверждением касания работает тактильный щелчок.
   */
  const selectChatFilter = useCallback((next: ChatFilter) => {
    hapticSelection();
    setChatFilter((prev) => (prev === next ? 'ALL' : next));
  }, []);

  useEffect(() => {
    if (!channelsHubOpen) return;
    const timer = setTimeout(() => {
      void loadChannelsDirectory(channelHubQuery);
    }, 280);
    return () => clearTimeout(timer);
    // loadChannelsDirectory стабилен (useCallback от t) — таймер дебаунса сбрасывает только запрос.
  }, [channelHubQuery, channelsHubOpen, loadChannelsDirectory]);

  const openNewMessage = useCallback(
    (params?: RootStackParamList['NewMessage']) => {
      hapticSelection();
      Keyboard.dismiss();
      navigation.navigate('NewMessage', params);
    },
    [navigation],
  );

  /**
   * Карточка человека как профиль (вариант A): с аватарки личного чата и со своей аватарки в
   * шапке. Какие у неё кнопки, решает то, кем человек приходится, — а не то, откуда открыли.
   */
  const profile = useProfileCard(navigation, {
    onWrite: (person) => {
      const existing = items.find((c) => c.type === 'DIRECT' && c.peerUserId === person.userId);
      if (existing) {
        openChat(existing);
        return;
      }
      void createDirect(person.userId)
        .then((chat) => navigation.navigate('Chat', chatNavParamsFromSummary(chat)))
        .catch((e: unknown) => tell(t, t('errorTitle'), humanizeError(e, t('openChatFailed'))));
    },
    onShareContact: (publicProfile, person) =>
      setShareContactTarget(shareTargetFromProfile(publicProfile, person.displayName)),
  });
  const openProfile = profile.open;

  function openChannelsHub() {
    setChannelsHubOpen(true);
    void loadChannelsDirectory(channelHubQuery);
  }

  function closeChannelsHub() {
    setChannelsHubOpen(false);
    setChannelHubQuery('');
  }

  // Открыть каталог по отметке из параметров маршрута — один раз на каждую новую отметку.
  useEffect(() => {
    if (openHubAt == null || handledHubAtRef.current === openHubAt) return;
    handledHubAtRef.current = openHubAt;
    setChannelsHubOpen(true);
    void loadChannelsDirectory('');
  }, [loadChannelsDirectory, openHubAt]);

  function closeChannelPreview(options?: { returnToHub?: boolean }) {
    setPreviewChannel(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
    // Вернуть каталог только при «Закрыть»: мы закрывали хаб, чтобы не стакать Modal.
    if (options?.returnToHub !== false) {
      setChannelsHubOpen(true);
    }
  }

  async function subscribeFromPreview(channel: ChannelDirectoryItem) {
    try {
      await subscribeChannel(channel.id);
      track('channel_subscribed', { source: 'preview' });
      closeChannelPreview({ returnToHub: false });
      closeChannelsHub();
      await loadChannelsDirectory(channelHubQuery);
      await load();
      navigation.navigate('Chat', {
        chatId: channel.id,
        title: channel.title,
        isGroup: false,
        isChannel: true,
        canPost: channel.myRole === 'ADMIN' || channel.myRole === 'EDITOR',
        canComment: channel.allowSubscriberComments,
        canReact: channel.allowSubscriberReactions,
        canManage: channel.canManage,
        myRole: channel.myRole ?? null,
        channelDescription: channel.description ?? null,
        memberCount: channel.memberCount,
        chatHasAvatar: Boolean(channel.chatHasAvatar),
        chatAvatarRevision:
          typeof channel.chatAvatarRevision === 'number' && Number.isFinite(channel.chatAvatarRevision)
            ? channel.chatAvatarRevision
            : 0,
        chatAvatarShape: channel.chatAvatarShape ?? null,
      });
    } catch (e) {
      setPreviewError(humanizeError(e, t('channelSubscriptionFailed')));
    }
  }

  async function toggleChannelSubscription(channel: ChannelDirectoryItem) {
    try {
      if (channel.subscribed) {
        await unsubscribeChannel(channel.id);
        track('channel_unsubscribed', { source: 'directory' });
      } else {
        await subscribeChannel(channel.id);
        track('channel_subscribed', { source: 'directory' });
      }
      await loadChannelsDirectory(channelHubQuery);
      await load();
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('channelSubscriptionFailed')));
    }
  }

  async function openManageChannel(channel: ChannelDirectoryItem) {
    // Не стакаем manage поверх хаба — на Android второй Modal часто не показывается.
    setChannelsHubOpen(false);
    setManageChannel(channel);
    setManageName(channel.title);
    setManageDescription(channel.description ?? '');
    setManageSlug(channel.slug ?? '');
    setManageSlugError(null);
    setManageAllowReactions(channel.allowSubscriberReactions);
    setManageAllowComments(channel.allowSubscriberComments);
    resetManageEditorSearch();
    try {
      const editors = await fetchChannelEditors(channel.id);
      setManageEditors(editors);
    } catch {
      setManageEditors([]);
    }
    setChannelManageOpen(true);
    // Окно открывается с пустой строкой поиска: ответ поиска, опоздавший к прошлому закрытию, не
    // должен в нём всплыть.
    resetManageEditorSearch();
  }

  function closeManageChannel() {
    setChannelManageOpen(false);
    setManageChannel(null);
    setManageEditors([]);
    resetManageEditorSearch();
  }

  async function saveManageChannel() {
    if (!manageChannel) return;
    const slug = normalizePublicHandleInput(manageSlug);
    if (manageSlug.trim().length > 0 && !isValidPublicHandle(slug)) {
      setManageSlugError(t('channelSlugInvalid'));
      return;
    }
    setManageSlugError(null);
    try {
      await updateChannel(manageChannel.id, {
        name: manageName.trim(),
        description: manageDescription.trim(),
        allowSubscriberReactions: manageAllowReactions,
        allowSubscriberComments: manageAllowComments,
        ...(slug ? { slug } : {}),
      });
      await loadChannelsDirectory(channelHubQuery);
      await load();
      closeManageChannel();
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('channelUpdateFailed')));
    }
  }

  async function addEditorToManagedChannel(peer: UserPublic) {
    if (!manageChannel) return;
    try {
      await addChannelEditor(manageChannel.id, peer.id);
      const editors = await fetchChannelEditors(manageChannel.id);
      setManageEditors(editors);
      resetManageEditorSearch();
      await loadChannelsDirectory(channelHubQuery);
      await load();
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('channelEditorAddFailed')));
    }
  }

  async function removeEditorFromManagedChannel(peer: UserPublic) {
    if (!manageChannel) return;
    try {
      await removeChannelEditor(manageChannel.id, peer.id);
      const editors = await fetchChannelEditors(manageChannel.id);
      setManageEditors(editors);
      await loadChannelsDirectory(channelHubQuery);
      await load();
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('channelEditorRemoveFailed')));
    }
  }

  async function performDeleteChannel(channelId: string) {
    try {
      await deleteChat(channelId);
      setItems((prev) => prev.filter((x) => x.id !== channelId));
      showToast({ message: t('chatListDeletedToast') });
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('deleteChatFailed')));
    }
  }

  function closeBossModal() {
    setBossOpen(false);
    setUserToDelete(null);
    setBossUserCard(null);
    setBossNewLogin('');
    setBossNewName('');
    setBossNewPassword('');
    setBossLastCredentials(null);
    setBossError(null);
  }

  async function submitBossCreate() {
    const login = bossNewLogin.trim();
    const displayName = bossNewName.trim();
    const password = bossNewPassword;
    if (login.length < 2 || password.length < 8 || !displayName) {
      setBossError(t('errorGeneric'));
      return;
    }
    setBossBusy(true);
    setBossError(null);
    try {
      await bossCreateUser({
        login,
        password,
        displayName,
        instructionLocale: bossInstructionLocale,
      });
      setBossLastCredentials({ login, password, instructionLocale: bossInstructionLocale });
      setBossNewLogin('');
      setBossNewName('');
      setBossNewPassword(generateTemporaryPassword());
      await loadBossUsers();
    } catch (e) {
      setBossError(humanizeError(e, t('bossCreateUserFailed')));
    } finally {
      setBossBusy(false);
    }
  }

  /**
   * Карточка пользователя из админки BOSS. Строка списка знает только login и имя,
   * поэтому аватар и хэндл дотягиваем публичным профилем; если запрос не прошёл,
   * карточка всё равно открывается — с тем, что есть.
   */
  const openBossUserCard = useCallback((row: BossUserRow) => {
    // `canViewPhoto` здесь намеренно не ставится: список заведённых пользователей — это
    // администрирование, а не дружба, и фото во весь экран владельцу оно не открывает.
    setBossUserCard({ userId: row.id, displayName: row.displayName || row.login });
    void getUserPublicProfile(row.id)
      .then((publicProfile) => {
        setBossUserCard((prev) =>
          prev && prev.userId === row.id
            ? {
                ...prev,
                displayName: publicProfile.displayName || prev.displayName,
                hasAvatar: publicProfile.hasAvatar,
                revision: publicProfile.avatarRevision ?? 0,
                shapeId: publicProfile.avatarShape,
                publicHandle: publicProfile.publicHandle,
                bio: publicProfile.bio ?? null,
                statusText: publicProfile.statusText ?? null,
              }
            : prev,
        );
      })
      .catch(() => {
        /* карточка остаётся с данными из строки списка */
      });
  }, []);

  async function performDeleteBossUser(row: BossUserRow) {
    try {
      await bossDeleteUser(row.id);
      setUserToDelete(null);
      await loadBossUsers();
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('bossDeleteUserFailed')));
    }
  }

  // ——— Меню долгого нажатия ———

  const openRowMenu = useCallback((chat: ChatSummary) => {
    hapticMedium();
    Keyboard.dismiss();
    setMenuPage('main');
    setMenuConfirm(null);
    setDeleteForPeerToo(false);
    setMenuChat(chat);
  }, []);

  const closeRowMenu = useCallback(() => {
    setMenuChat(null);
    setMenuBusy(false);
  }, []);

  /**
   * Заглушить или вернуть звук — сразу, с «Отменить» в тосте, без окна, которое надо закрыть
   * (п. 13). Настройка на сервере: она переезжает между устройствами и работает на iOS, где
   * уведомление рисует система. Звонков заглушение не касается.
   */
  /**
   * Запросы звука одного чата — строго по очереди. «Отменить» нажимают сразу, пока первый
   * запрос ещё летит; два параллельных PATCH сервер мог применить в обратном порядке, и звук
   * оставался выключенным (прогон 2026-10-05, сценарий 19).
   */
  const muteQueueRef = useRef(new Map<string, Promise<unknown>>());
  const queueMute = useCallback((chatId: string, muted: boolean, until: Date | null) => {
    const prev = muteQueueRef.current.get(chatId) ?? Promise.resolve();
    const run = prev.catch(() => undefined).then(() => setChatMuted(chatId, muted, until));
    muteQueueRef.current.set(chatId, run);
    return run;
  }, []);

  const applyMute = useCallback(
    (chat: ChatSummary, duration: MuteDuration | null, options?: { undoable?: boolean }) => {
      const previous = muteOf(chat);
      const until = duration ? muteUntil(duration) : null;
      const next: MuteState = { muted: duration != null, mutedUntil: until ? until.toISOString() : null };
      setMuteOverrides((prev) => ({ ...prev, [chat.id]: next }));
      queueMute(chat.id, next.muted, until)
        .then((saved) => {
          setItems((prev) =>
            prev.map((c) => (c.id === chat.id ? { ...c, muted: saved.muted, mutedUntil: saved.mutedUntil ?? null } : c)),
          );
        })
        .catch((e: unknown) => {
          setMuteOverrides((prev) => ({ ...prev, [chat.id]: previous }));
          tell(t, t('errorTitle'), humanizeError(e, t('chatListMuteFailed')));
        });
      if (options?.undoable === false) return;
      const message =
        duration === '1h'
          ? t('chatListMutedToast1h')
          : duration === '8h'
            ? t('chatListMutedToast8h')
            : duration === 'forever'
              ? t('chatListMutedToastForever')
              : t('chatListUnmutedToast');
      showToast({
        message,
        actionLabel: t('actionUndo'),
        onAction: () => {
          // «Отменить» возвращает ровно прежнее: тот же срок, а не «навсегда».
          setMuteOverrides((prev) => ({ ...prev, [chat.id]: previous }));
          queueMute(chat.id, previous.muted, previous.mutedUntil ? new Date(previous.mutedUntil) : null)
            .then((saved) =>
              setItems((prev) =>
                prev.map((c) => (c.id === chat.id ? { ...c, muted: saved.muted, mutedUntil: saved.mutedUntil ?? null } : c)),
              ),
            )
            .catch((e: unknown) => tell(t, t('errorTitle'), humanizeError(e, t('chatListMuteFailed'))));
        },
      });
    },
    [muteOf, queueMute, t],
  );

  const markRead = useCallback(
    (chat: ChatSummary) => {
      setUnreadByChatId((prev) => {
        if (!prev[chat.id]) return prev;
        const next = { ...prev };
        delete next[chat.id];
        return next;
      });
      // Сервер тоже узнаёт: иначе на другом устройстве чат остался бы непрочитанным.
      markChatRead(chat.id).catch(() => {
        /* локальный счётчик уже снят; серверную отметку догонит следующее открытие чата */
      });
    },
    [],
  );

  const runMenuItem = useCallback(
    (chat: ChatSummary, item: ChatRowMenuItem) => {
      const peer = chatTileCallPeerId(chat);
      switch (item) {
        case 'mute':
          setMenuPage('mute');
          return;
        case 'unmute':
          closeRowMenu();
          applyMute(chat, null);
          return;
        case 'markRead':
          closeRowMenu();
          markRead(chat);
          return;
        case 'callAudio':
        case 'callVideo':
          closeRowMenu();
          if (peer) startCallFromChat(chat, peer, item === 'callVideo' ? 'video' : 'audio');
          return;
        case 'deleteForMe':
          setDeleteForPeerToo(false);
          setMenuPage('deleteDirect');
          return;
        default:
          setMenuConfirm(item);
          setMenuPage('confirm');
      }
    },
    [applyMute, closeRowMenu, markRead],
  );

  /** Удалить личный чат: по умолчанию только у себя, галочкой — у обоих (п. 9). */
  const performDeleteDirect = useCallback(
    async (chat: ChatSummary, forBoth: boolean) => {
      setMenuBusy(true);
      try {
        if (forBoth) await deleteChat(chat.id);
        else await hideChatForMe(chat.id);
        setItems((prev) => prev.filter((x) => x.id !== chat.id));
        closeRowMenu();
        showToast({ message: t('chatListDeletedToast') });
      } catch (e) {
        setMenuBusy(false);
        tell(t, t('errorTitle'), humanizeError(e, t('deleteChatFailed')));
      }
    },
    [closeRowMenu, t],
  );

  /** Выйти из группы, отписаться, удалить группу или канал у всех — после подтверждения. */
  const performConfirmed = useCallback(
    async (chat: ChatSummary, action: RowConfirm) => {
      setMenuBusy(true);
      const failKey: MessageKey =
        action === 'unsubscribe' ? 'channelUnsubscribeFailed' : action === 'leaveGroup' ? 'groupLeaveFailed' : 'deleteChatFailed';
      try {
        if (action === 'unsubscribe') await unsubscribeChannel(chat.id);
        else if (action === 'leaveGroup') await leaveGroup(chat.id);
        else await deleteChat(chat.id);
        setItems((prev) => prev.filter((x) => x.id !== chat.id));
        closeRowMenu();
        showToast({
          message:
            action === 'unsubscribe'
              ? t('chatListUnsubscribedToast')
              : action === 'leaveGroup'
                ? t('chatListLeftToast')
                : t('chatListDeletedToast'),
        });
      } catch (e) {
        setMenuBusy(false);
        tell(t, t('errorTitle'), humanizeError(e, t(failKey)));
      }
    },
    [closeRowMenu, t],
  );

  const visibleChats = useMemo(
    () => searchChats(visibleChatsOf(items, chatFilter, chatSort, chatListViewPrefsHydrated), query),
    [chatFilter, chatListViewPrefsHydrated, chatSort, items, query],
  );
  const searching = query.trim().length > 0;
  /** Новичок без переписки — ему пустое состояние с «Найти людей», а не голое «Чатов нет». */
  const hasAnyChat = useMemo(() => items.some((c) => c.type !== 'SAVED'), [items]);
  const sortLabel = t(SORT_OPTIONS.find((o) => o.sort === chatSort)?.key ?? 'chatSortRecent');

  /**
   * «Избранное» (эпик 8, стори 8.2). Карточка показывается всегда, даже когда чата ещё нет:
   * сервер заводит его лениво, при первом обращении, и ждать первого сохранённого сообщения,
   * чтобы показать вход в раздел, значило бы прятать раздел от того, кто его не открывал.
   */
  const savedChatSummary = useMemo(() => savedChatOf(items), [items]);
  const [savedOpening, setSavedOpening] = useState(false);
  const openSavedChat = useCallback(async () => {
    if (savedOpening) return;
    hapticSelection();
    setSavedOpening(true);
    try {
      // Даже когда карточка уже есть в списке, чат перечитывается: ответ «получи или заведи»
      // идемпотентен, а список мог устареть с прошлой загрузки.
      const chat = await fetchSavedChat();
      setItems((prev) => (prev.some((c) => c.id === chat.id) ? prev : [...prev, chat]));
      navigation.navigate('Chat', chatNavParamsFromSummary(chat));
    } catch (e) {
      tell(t, t('errorTitle'), humanizeError(e, t('savedOpenFailed')));
    } finally {
      setSavedOpening(false);
    }
  }, [navigation, savedOpening, t]);

  /**
   * Подпись под карточкой: последняя заметка, а пока её нет — объяснение, что это за место.
   * Целиком тем же путём, что и у обычных строк (`resolveChatPreview` + `ChatPreviewLine`):
   * раньше здесь брали только `.text`, поэтому сообщение-стикер терялось совсем, а фото без
   * подписи показывалось служебным «[image]».
   */
  const savedCardPreview = useMemo<ResolvedChatPreview>(() => {
    const hint = t('savedChatCardHint');
    if (!savedChatSummary) return { text: hint, marker: null };
    // Пустой текст без маркера отсюда не выходит: `resolveChatPreview` либо отдаёт маркер,
    // либо падает на fallback (сам `hint`), — отдельной ветки под этот случай не нужно.
    return previewForDisplay(resolveChatPreview(savedChatSummary, hint), t);
  }, [savedChatSummary, t]);

  const savedChatCard = useMemo(
    () => (
      <View style={[styles.item, paneSavedOpen ? styles.itemSelected : null]}>
        <View style={[styles.itemAccent, styles.itemAccentSaved]} />
        <AppPressable
          style={styles.itemOpenArea}
          onPress={() => void openSavedChat()}
          accessibilityRole="button"
          accessibilityLabel={t('savedChatTitle')}>
          <View style={styles.itemTitleRow}>
            {/* Компас в размер аватарки соседних строк: слот и так 38, иконка встаёт вровень. */}
            <View style={styles.savedCardIconSlot}>
              <SavedChatIcon size={38} />
            </View>
            <Text style={styles.itemTitle} numberOfLines={1}>
              {t('savedChatTitle')}
            </Text>
            {savedOpening ? <ActivityIndicator color={chatListHeaderIconTint} /> : null}
          </View>
          {/* Счётчика непрочитанного здесь нет и быть не может: непрочитанного у себя не бывает. */}
          {/*
            Две строки, а не одна: `savedChatCardHint` — целая фраза, и обрезать её многоточием
            было бы хуже, чем перенести. Пульсацию глифа здесь гасим: карточка приколота сверху
            списка навсегда, вечная анимация ей ни к чему.
          */}
          <ChatPreviewLine preview={savedCardPreview} styles={styles} numberOfLines={2} animated={false} />
        </AppPressable>
      </View>
    ),
    [chatListHeaderIconTint, openSavedChat, paneSavedOpen, savedCardPreview, savedOpening, styles, t],
  );

  /**
   * Шапка списка: «Избранное» (кроме поиска, где оно мешает найденному) и — под фильтром
   * «Каналы» — вход в каталог: прежняя кнопка-рупор ушла из шапки, а каналы обязаны остаться
   * в одном касании от списка.
   */
  const listHeader = (
    <View>
      {!searching ? savedChatCard : null}
      {chatFilter === 'CHANNEL' && !searching ? (
        <AppPressable
          style={styles.linkRow}
          onPress={openChannelsHub}
          accessibilityRole="button"
          accessibilityLabel={t('chatListChannelCatalog')}>
          <MegaphoneGlyph color={chatListHeaderIconTint} />
          <Text style={styles.linkRowText}>{t('chatListChannelCatalog')}</Text>
        </AppPressable>
      ) : null}
    </View>
  );

  /** Строка «Найти людей по запросу» — поиск по чатам не находит тех, с кем ещё не писались. */
  const searchPeopleRow = searching ? (
    <AppPressable
      style={styles.linkRow}
      onPress={() => openNewMessage({ query: query.trim() })}
      accessibilityRole="button">
      <SearchGlyph color={chatListHeaderIconTint} />
      <Text style={styles.linkRowText} numberOfLines={2}>
        {fillTemplate(t('chatListSearchPeople'), { query: query.trim() })}
      </Text>
    </AppPressable>
  ) : null;

  // Стабилизируем renderItem — без useCallback его идентичность меняется на каждый
  // ререндер (например, при наборе текста в поиске), что заставляет FlatList
  // пересоздавать все видимые ячейки.
  const renderChatItem = useCallback(
    ({ item }: { item: ChatSummary }) => {
      const muted = isMuted(item);
      return (
        <View style={[styles.item, paneSelectedChatId === item.id ? styles.itemSelected : null]}>
          <View
            style={[
              styles.itemAccent,
              item.type === 'CHANNEL'
                ? styles.itemAccentChannel
                : item.type === 'GROUP'
                  ? styles.itemAccentGroup
                  : styles.itemAccentDirect,
            ]}
          />
          <AppPressable
            style={styles.itemOpenArea}
            onPress={() => openChat(item)}
            /*
              Удержание любой строки — меню действий (п. 2 аудита): звук, звонок, удалить, выйти.
              Раньше удержание работало только у личной переписки, а у группы и канала не делало
              ничего — жест, срабатывающий через раз, читается как поломка.
            */
            onLongPress={() => openRowMenu(item)}
            delayLongPress={280}>
            <View style={styles.itemTitleRow}>
              {item.type === 'DIRECT' && item.peerUserId ? (
                <AppPressable
                  onPress={() => {
                    const uid = item.peerUserId;
                    if (!uid) return;
                    openProfile(
                      {
                        userId: uid,
                        displayName: item.title,
                        hasAvatar: item.peerHasAvatar,
                        avatarRevision: item.peerAvatarRevision ?? 0,
                        avatarShape: item.peerAvatarShape,
                      },
                      // Личная переписка бывает и с тем, кого потом убрали из контактов (у
                      // «Босса» — и вовсе с кем угодно), поэтому спрашиваем список, а не
                      // считаем собеседника другом по факту чата.
                      relationOf(uid, contacts.relation),
                    );
                  }}
                  hitSlop={6}
                  accessibilityRole="button"
                  accessibilityLabel={t('userCardTitle')}>
                  <AvatarBadge
                    userId={item.peerUserId}
                    displayName={item.title}
                    hasAvatar={item.peerHasAvatar}
                    revision={item.peerAvatarRevision ?? 0}
                    shapeId={item.peerAvatarShape}
                    size={38}
                  />
                </AppPressable>
              ) : item.type === 'GROUP' || item.type === 'CHANNEL' ? (
                <AvatarBadge
                  chatAvatarChatId={item.id}
                  displayName={item.title}
                  hasAvatar={item.chatHasAvatar}
                  revision={item.chatAvatarRevision ?? 0}
                  shapeId="circle"
                  size={38}
                />
              ) : null}
              {item.type === 'DIRECT' ? (
                <View
                  style={[
                    styles.presenceDot,
                    resolveDirectPresence(item) ? styles.presenceDotOnline : styles.presenceDotOffline,
                  ]}
                />
              ) : null}
              {/*
                Заголовок в одну строку с многоточием: на кегле 17 длинное имя чата
                («Squad Alpha Renamed») переносилось на три строки и распирало карточку.
                Обрезка — то, что делают все системные списки сообщений: высота строки
                списка обязана быть предсказуемой, а полное имя всё равно видно в чате.
              */}
              <Text style={styles.itemTitle} numberOfLines={1}>
                {item.title}
              </Text>
              {/* Значок «без звука» вместо кнопки-колокольчика на каждой строке (п. 13). */}
              {muted ? (
                <View style={styles.mutedIcon} accessible accessibilityLabel={t('chatListMuted')}>
                  <MutedBellGlyph color={chatListHeaderIconTint} />
                </View>
              ) : null}
              {item.type === 'GROUP' && (() => {
                const groupOnlineCount = resolveGroupOnlineCount(item);
                if (groupOnlineCount == null) return null;
                return (
                <View style={styles.groupOnlineBadge}>
                  <Text style={styles.groupOnlineBadgeText}>{groupOnlineCount} {t('onlineNow')}</Text>
                </View>
                );
              })()}
              {unreadByChatId[item.id] ? (
                <View style={styles.unreadBadge}>
                  <Text style={styles.unreadBadgeText}>{Math.min(99, unreadByChatId[item.id])}</Text>
                </View>
              ) : null}
            </View>
            {item.type === 'GROUP' ? (
              <Text style={styles.itemMeta}>{t('chatTypeGroup')}</Text>
            ) : item.type === 'CHANNEL' ? (
              <Text style={styles.itemMeta}>{t('chatTypeChannel')}</Text>
            ) : null}
            <ChatPreviewLine
              preview={
                outboxPreview[item.id]
                  ? { text: outboxPreview[item.id], marker: null }
                  : drafts[item.id]?.trim()
                  ? { text: draftPreviewLine(drafts[item.id], t), marker: null }
                  : previewForDisplay(resolveChatPreview(item, t('chatLastMessageFallback')), t)
              }
              styles={styles}
              numberOfLines={1}
            />
          </AppPressable>
          {item.unseenReactionEmoji || (item.type === 'DIRECT' && item.directMessagesNextPurgeAt) ? (
            <View style={styles.tileActions}>
              {/*
                Значок непросмотренной реакции — новость, которую читают, а не кнопка. Ключ по
                эмодзи перезапускает пульс, когда поверх старой реакции приходит новая. Кнопки
                удаления и звука со строки ушли в меню удержания (п. 2 и 13 аудита).
              */}
              {item.unseenReactionEmoji ? (
                <UnseenReactionBadge
                  key={item.unseenReactionEmoji}
                  emoji={item.unseenReactionEmoji}
                  color={chatListHeaderIconTint}
                  accessibilityLabel={t('unseenReactionA11y').replace('{emoji}', item.unseenReactionEmoji)}
                />
              ) : null}
              {item.type === 'DIRECT' && item.directMessagesNextPurgeAt ? (
                <DirectRetentionCountdown
                  purgeAtIso={item.directMessagesNextPurgeAt}
                  policyTitle={t('directRetentionPolicyTitle')}
                  policyBody={t('directRetentionPolicyBody')}
                  confirmLabel={t('close')}
                  a11yLabel={t('directRetentionTimerA11y')}
                  styles={styles}
                />
              ) : null}
            </View>
          ) : null}
        </View>
      );
    },
    [
      styles,
      drafts,
      outboxPreview,
      t,
      openChat,
      openRowMenu,
      isMuted,
      openProfile,
      contacts.relation,
      resolveDirectPresence,
      resolveGroupOnlineCount,
      unreadByChatId,
      chatListHeaderIconTint,
      paneSelectedChatId,
    ],
  );

  /**
   * Пустой список — три разных случая, и путать их нельзя (п. 11 и P0-6 аудита):
   * загрузка — каркас; ошибка — сказать об этом и дать «Повторить»; настоящая пустота — у
   * новичка карточка с «Найти людей» и «Пригласить», а под фильтром или поиском — строка.
   */
  let listEmpty: React.ReactElement;
  if (loading || !chatListViewPrefsHydrated) {
    listEmpty = <ChatListSkeleton cardColor={listPal.item} blockColor={listPal.skeletonBlock} />;
  } else if (loadError != null && !hasAnyChat) {
    listEmpty = (
      <View style={styles.stateCard}>
        <Text style={styles.stateCardTitle}>{t('chatListLoadFailed')}</Text>
        <Text style={styles.stateCardBody}>{humanizeError(loadError, t('errorSomethingWrong'))}</Text>
        <View style={styles.stateCardActions}>
          <AppPressable style={styles.pillPrimary} onPress={() => void load('initial')} accessibilityRole="button">
            <Text style={styles.pillPrimaryText}>{t('actionRetry')}</Text>
          </AppPressable>
        </View>
      </View>
    );
  } else if (searching) {
    listEmpty = (
      <View>
        <Text style={styles.empty}>{fillTemplate(t('chatListSearchNothing'), { query: query.trim() })}</Text>
        {searchPeopleRow}
      </View>
    );
  } else if (!hasAnyChat) {
    listEmpty = (
      <View style={styles.stateCard}>
        <Text style={styles.stateCardTitle}>{t('chatListEmptyTitle')}</Text>
        <Text style={styles.stateCardBody}>{t('chatListEmptyBody')}</Text>
        <View style={styles.stateCardActions}>
          <AppPressable style={styles.pillPrimary} onPress={() => openNewMessage()} accessibilityRole="button">
            <Text style={styles.pillPrimaryText}>{t('chatListEmptyFindPeople')}</Text>
          </AppPressable>
          <AppPressable style={styles.pillSecondary} onPress={() => setInviteOpen(true)} accessibilityRole="button">
            <Text style={styles.pillSecondaryText}>{t('chatListEmptyInvite')}</Text>
          </AppPressable>
        </View>
      </View>
    );
  } else {
    listEmpty = <Text style={styles.empty}>{t('noChatsHint')}</Text>;
  }

  /**
   * «+» — одна точка входа вместо трёх кнопок шапки (вариант A). На Android — плавающая
   * кнопка, как велит Material; на iOS и в вебе — карандаш в шапке, как в «Сообщениях» и Telegram.
   */
  const composeAsFab = Platform.OS === 'android';
  const menuItems = menuChat
    ? chatRowMenuItems(menuChat, { muted: isMuted(menuChat), unread: unreadByChatId[menuChat.id] ?? 0 })
    : [];

  const menuItemLabel = (item: ChatRowMenuItem): string => {
    switch (item) {
      case 'mute':
        return t('chatListMuteAction');
      case 'unmute':
        return t('chatListUnmute');
      case 'markRead':
        return t('chatListMarkRead');
      case 'callAudio':
        return t('chatTileCallAudio');
      case 'callVideo':
        return t('chatTileCallVideo');
      case 'deleteForMe':
        return t('chatListDeleteChat');
      case 'leaveGroup':
        return t('groupLeaveBtn');
      case 'unsubscribe':
        return t('channelUnsubscribe');
      case 'deleteGroup':
        return t('chatListDeleteGroup');
      default:
        return t('deleteChannel');
    }
  };

  return (
    <View style={styles.screenRoot}>
      {profile.element}
      {/**
       * `bgImage` is wrapped in try/catch around the require() so that a
       * missing/corrupt PNG cannot leave the screen looking blank — if the
       * background fails to load the dark `screenRoot` color still shows.
       */}
      {chatListWallpaper ? (
        <Image
          source={chatListWallpaper}
          style={[
            styles.bgImage,
            {
              width: bgW,
              height: bgH,
              left: (window.width - bgW) / 2,
              top: (window.height - bgH) / 2,
            },
          ]}
          resizeMode="cover"
          onError={(e) => {
            if (__DEV__) {
              console.debug('[ChatListScreen] background image failed to load', e?.nativeEvent);
            }
          }}
        />
      ) : null}
      {/**
       * SVG-rendered wallpaper for themes that don't ship a bitmap (sun,
       * cosmos). Sized to cover the screen the same way the image wallpaper
       * does, so portrait/landscape rotation looks identical.
       */}
      {chatListWallpaperRenderer ? (
        <View
          pointerEvents="none"
          style={[
            styles.bgImage,
            {
              width: bgW,
              height: bgH,
              left: (window.width - bgW) / 2,
              top: (window.height - bgH) / 2,
            },
          ]}>
          {chatListWallpaperRenderer(bgW, bgH)}
        </View>
      ) : null}
      <View style={[styles.scrim, { pointerEvents: 'none' }]} />
      {__DEV__ ? (
        <View
          style={[styles.aliveDot, { pointerEvents: 'none' }]}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        />
      ) : null}
      <View style={[styles.content, { paddingBottom: insets.bottom }]}>
      {/*
        Шапка лежит НАД списком, а не в колонке перед ним: только так под ней есть что
        размывать. Список получает верхний отступ ровно в её высоту (см. `barHeight`), поэтому
        внизу ничего не теряется, а при прокрутке карточки уезжают под матовое стекло.
      */}
      <FrostedBar
        tint={listPal.barTint}
        fallbackColor={listPal.bar}
        style={styles.floatingBar}
        onLayout={(e) => setBarHeight(e.nativeEvent.layout.height)}>
      {/*
        Узор идёт первым потомком: он должен лежать НА матовой заливке, но ПОД заголовком и
        кнопками — иначе подписи пришлось бы читать сквозь конверты.
      */}
      <HeaderPattern
        kind={listPal.headerPattern}
        tint={listPal.headerPatternTint}
        opacity={listPal.headerPatternOpacity}
      />
      <View style={[styles.header, { paddingTop: Math.max(52, insets.top + 12) }]}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexShrink: 1 }}>
          {user ? (
            <AppPressable
              onPress={() =>
                profile.open(
                  {
                    userId: user.userId,
                    displayName: user.displayName,
                    hasAvatar: user.hasAvatar,
                    avatarRevision: user.avatarRevision ?? 0,
                    avatarShape: user.avatarShape,
                    publicHandle: user.publicHandle ?? null,
                  },
                  // Своё фото — всегда: правило про друзей защищает чужие снимки, не свои.
                  'self',
                )
              }
              hitSlop={6}
              accessibilityRole="button"
              accessibilityLabel={t('userCardTitle')}>
              <AvatarBadge
                userId={user.userId}
                displayName={user.displayName}
                hasAvatar={user.hasAvatar}
                revision={user.avatarRevision}
                shapeId={user.avatarShape}
                size={44}
              />
            </AppPressable>
          ) : null}
          {/*
            Заголовка «Чаты» здесь нет намеренно (решение владельца): экран и так открыт на
            списке чатов. Осталось имя владельца аккаунта — и под ним строка, которой есть что
            сказать: статус, ник или приглашение статус поставить.

            Нажимается вся пара, а не одна аватарка: раньше тап по имени не делал ничего, хотя
            стоял вплотную к нажимаемому снимку, — промах по 44 пикселям выглядел как поломка.
            Аватарка ведёт в свою карточку (как и была), имя — в лист «Статус и оформление».
          */}
          <AppPressable
            onPress={() => setSelfStatusOpen(true)}
            hitSlop={6}
            style={{ flexShrink: 1 }}
            accessibilityRole="button"
            accessibilityLabel={t('quickSettingsTitle')}>
            <Text style={styles.headerSelfName} numberOfLines={1}>
              {user?.displayName ?? ''}
            </Text>
            <View style={styles.headerSelfStatusRow}>
              {user?.doNotDisturb ? (
                <View style={styles.headerSelfDndIcon}>
                  <BellGlyph size={15} strokeWidth={2.4} color={listPal.h2} />
                  <View style={styles.headerSelfDndSlash} />
                </View>
              ) : null}
              <Text style={styles.headerSelfStatus} numberOfLines={1}>
                {selfStatusLine}
              </Text>
            </View>
          </AppPressable>
        </View>
        <View style={styles.headerActions}>
          {user?.appRole === 'BOSS' ? (
            <AppPressable
              onPress={() => {
                setBossInstructionLocale(locale);
                // Свежий временный пароль на каждое открытие окна BOSS.
                setBossNewPassword(generateTemporaryPassword());
                setBossOpen(true);
                void loadBossUsers();
              }}
              hitSlop={8}
              style={styles.headerActionBtn}
              accessibilityRole="button"
              accessibilityLabel={t('bossUsersTitle')}>
              <AdminShieldGlyph color={chatListHeaderIconTint} />
            </AppPressable>
          ) : null}
          {/* Встречи по ссылке — только у организатора; сервер отказывает всем остальным. */}
          {canManageMeetings(user?.login) ? (
            <AppPressable
              onPress={() => navigation.navigate('Meetings')}
              hitSlop={8}
              style={styles.headerActionBtn}
              accessibilityRole="button"
              accessibilityLabel={t('meetingsOpenA11y')}
              testID="chat-list-meetings">
              <MeetingGlyph color={chatListHeaderIconTint} />
            </AppPressable>
          ) : null}
          {/*
            Лампочка «Подсказок и новостей» (заказ владельца 2026-08-31). Слева от шестерёнки:
            настройки — то, куда ходят по делу и знают куда, а сюда зовут, и звать надо ближе к
            середине шапки, а не с самого края.
          */}
          <HelpBulbButton
            unreadCount={helpCenter.unreadCount}
            onPress={openHelpCenter}
            tint={chatListHeaderIconTint}
            buttonStyle={styles.headerActionBtn}
            badgeStyle={styles.contactRequestBadge}
            badgeTextStyle={styles.contactRequestBadgeText}
          />
          {/*
            «Выйти» из шапки убран (п. 3 аудита): иконка рядом с настройками выкидывала из
            аккаунта одним касанием без вопроса. Выход теперь в «Настройках», с подтверждением.
          */}
          {/* Плеер — рядом с настройками (заказ владельца 2026-10-04). */}
          <MusicHeaderButton tint={chatListHeaderIconTint} style={styles.headerActionBtn} />
          <AppPressable
            onPress={() => setSettingsOpen(true)}
            hitSlop={8}
            style={styles.headerActionBtn}
            accessibilityRole="button"
            accessibilityLabel={t('gearSettings')}>
            <TintedIcon
              source={require('../../assets/chat-icons/header-settings.png')}
              style={styles.headerActionIconImage}
              tintColor={chatListHeaderIconTint}
            />
          </AppPressable>
          {!composeAsFab ? (
            <AppPressable
              onPress={() => openNewMessage()}
              hitSlop={8}
              style={styles.headerActionBtn}
              accessibilityRole="button"
              accessibilityLabel={t('chatListComposeA11y')}
              testID="chat-list-compose">
              <ComposeGlyph color={chatListHeaderIconTint} />
            </AppPressable>
          ) : null}
        </View>
      </View>
      {/* Связи с сервером нет дольше пары секунд — входящие не придут, пока не вернётся (UX-41). */}
      <Suspense fallback={null}>
        <ConnectionBanner textColor={chatListHeaderIconTint} />
      </Suspense>

      <View style={styles.searchBar}>
        <SearchGlyph color={chatListHeaderIconTint} />
        <TextInput
          ref={searchInputRef}
          value={query}
          onChangeText={setQuery}
          placeholder={t('chatListSearchPlaceholder')}
          placeholderTextColor={listPal.hint}
          style={styles.searchInput}
          maxLength={SEARCH_QUERY_MAX_CHARS}
          autoCorrect={false}
          returnKeyType="search"
          onSubmitEditing={() => Keyboard.dismiss()}
          accessibilityLabel={t('chatListSearchPlaceholder')}
          testID="chat-list-search"
        />
        {query ? (
          <AppPressable
            style={styles.searchClearBtn}
            onPress={() => setQuery('')}
            accessibilityRole="button"
            accessibilityLabel={t('chatListSearchClearA11y')}>
            <CrossGlyph color={chatListHeaderIconTint} size={16} />
          </AppPressable>
        ) : null}
      </View>

      {/*
        Заявки в контакты — тонкой плашкой над списком (п. 5), а не лампочкой на кнопке, которая
        выглядела так же, как «Советы и новости». Касание ведёт прямо к заявкам.
      */}
      {contacts.incoming.length > 0 ? (
        <AppPressable
          style={styles.requestsBanner}
          onPress={() => openNewMessage({ focus: 'requests' })}
          accessibilityRole="button"
          accessibilityLabel={fillTemplate(t('chatListRequestsBannerA11y'), { count: contacts.incoming.length })}
          testID="chat-list-requests-banner">
          <View style={styles.requestsBannerDot} />
          <Text style={styles.requestsBannerText} numberOfLines={1}>
            {fillTemplate(t('chatListRequestsBanner'), { count: contacts.incoming.length })}
          </Text>
          <Text style={styles.requestsBannerChevron}>›</Text>
        </AppPressable>
      ) : null}

      <View style={styles.chatControlsRow}>
        {FILTERS.map(({ filter, key }) => (
          <AppPressable
            key={filter}
            style={[styles.chatFilterChip, chatFilter === filter && styles.chatFilterChipActive]}
            onPress={() => selectChatFilter(filter)}
            accessibilityRole="button"
            accessibilityState={{ selected: chatFilter === filter }}
            accessibilityLabel={t(key)}>
            <Text
              style={[styles.chatFilterChipText, chatFilter === filter && styles.chatFilterChipTextActive]}
              numberOfLines={1}>
              {t(key)}
            </Text>
          </AppPressable>
        ))}
        {/* Не `flex: 0`: в вебе это `flex: 0 1 0%` — основа 0 перебивает ширину, и кнопка
            сжималась до полоски в 8 px у края ряда. Основу задаём явно: `flex: 1` у чипа тоже ставит 0 %. */}
        <AppPressable
          style={[styles.chatFilterChip, { flexGrow: 0, flexShrink: 0, flexBasis: 44, width: 44 }]}
          onPress={() => setSortMenuOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={fillTemplate(t('chatListSortA11y'), { value: sortLabel })}>
          <Text style={styles.chatSortBtnText}>⇅</Text>
        </AppPressable>
      </View>

      {/*
        Дни рождения контактов (заказ владельца 2026-08-29). Здесь, а не в «Избранном»:
        напоминание «сегодня день рождения» ценно ровно в тот день, когда его увидят сразу,
        а список чатов — первый экран. Панель сама решает, показываться ли, и молчит, когда
        поздравлять некого.
      */}
      <BirthdayRemindersPanel styles={styles} enabled={Boolean(user)} />
      </FrostedBar>

      <FlatList
        style={styles.list}
        contentContainerStyle={{ paddingTop: barHeight, paddingBottom: composeAsFab ? 88 : 0 }}
        data={visibleChats}
        keyExtractor={(c) => c.id}
        initialNumToRender={12}
        maxToRenderPerBatch={10}
        windowSize={7}
        updateCellsBatchingPeriod={50}
        removeClippedSubviews={Platform.OS !== 'web'}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        /*
          Полоска прокрутки справа убрана намеренно. Она рисуется системным светлым цветом
          поверх обоев и карточек — на тёмной теме это белая черта вдоль всего экрана,
          которую видно даже в покое на iOS. Длину списка чатов и так показывает сам список,
          а деления никто не отсчитывает.
        */
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            progressViewOffset={barHeight}
            refreshing={refreshing}
            onRefresh={() => {
              void load('pull');
              void refreshContacts();
            }}
          />
        }
        // Не первым элементом данных, а шапкой списка: так «Избранное» стоит сверху при любой
        // сортировке и любом фильтре (AC стори 8.2) и не участвует в них вовсе.
        ListHeaderComponent={listHeader}
        ListFooterComponent={visibleChats.length > 0 ? searchPeopleRow : null}
        renderItem={renderChatItem}
        ListEmptyComponent={listEmpty}
      />

      {composeAsFab ? (
        <AppPressable
          style={[styles.fab, { bottom: insets.bottom + SPACE.lg }]}
          onPress={() => openNewMessage()}
          accessibilityRole="button"
          accessibilityLabel={t('chatListComposeA11y')}
          testID="chat-list-compose">
          <PlusGlyph color="#fff" />
        </AppPressable>
      ) : null}

      {/* Лист выбора адресата для содержимого из системного «Поделиться» (стори 8.7). */}
      <ShareIntentSheet
        share={incomingShare}
        chats={items}
        palette={sheetPalette}
        onClose={() => setIncomingShare(null)}
        onDelivered={(chat) => {
          setIncomingShare(null);
          // Открываем чат, куда всё ушло: человек пришёл из другого приложения и должен увидеть
          // результат, а не вернуться к списку с одним лишь ощущением, что «вроде отправилось».
          openChat(chat);
        }}
      />

      <InviteSheet
        visible={inviteOpen}
        onClose={() => setInviteOpen(false)}
        handle={user?.publicHandle}
        styles={styles}
        palette={sheetPalette}
      />

      {/* Порядок списка: названия вместо значков, текущий отмечен. */}
      <BottomSheet
        visible={sortMenuOpen}
        onClose={() => setSortMenuOpen(false)}
        palette={sheetPalette}
        title={t('chatListSortTitle')}
        contentStyle={{ gap: SPACE.sm }}>
        {SORT_OPTIONS.map(({ sort, key }) => (
          <AppPressable
            key={sort}
            style={styles.menuRow}
            onPress={() => {
              hapticSelection();
              setChatSort(sort);
              setSortMenuOpen(false);
            }}
            accessibilityRole="radio"
            accessibilityState={{ checked: chatSort === sort }}>
            <Text style={styles.menuRowText}>{t(key)}</Text>
            {chatSort === sort ? <CheckGlyph color={listPal.chatFilterChipTextActive} /> : null}
          </AppPressable>
        ))}
      </BottomSheet>

      {/*
        Меню удержания строки. Нижний лист, а не системное меню: длинный тап в списке — жест
        «покажи, что тут ещё можно», и ответ на него приезжает снизу, оттуда же, откуда все
        остальные листы этого экрана. Подтверждения — страницами того же листа: второе окно
        поверх листа на Android уходит под него.
      */}
      <BottomSheet
        visible={menuChat != null}
        onClose={closeRowMenu}
        palette={sheetPalette}
        accessibilityLabel={t('chatListMenuA11y')}
        contentStyle={{ gap: SPACE.sm }}>
        {menuChat ? (
          <View style={styles.menuTitleRow}>
            <AvatarBadge
              {...(menuChat.type === 'DIRECT'
                ? {
                    userId: menuChat.peerUserId ?? undefined,
                    hasAvatar: menuChat.peerHasAvatar,
                    revision: menuChat.peerAvatarRevision ?? 0,
                    shapeId: menuChat.peerAvatarShape,
                  }
                : {
                    chatAvatarChatId: menuChat.id,
                    hasAvatar: menuChat.chatHasAvatar,
                    revision: menuChat.chatAvatarRevision ?? 0,
                    shapeId: 'circle',
                  })}
              displayName={menuChat.title}
              size={40}
            />
            <Text style={[styles.modalTitle, { flex: 1 }]} numberOfLines={1}>
              {menuChat.title}
            </Text>
          </View>
        ) : null}
        {menuChat && menuPage === 'main'
          ? menuItems.map((item) => {
              const danger =
                item === 'deleteForMe' ||
                item === 'leaveGroup' ||
                item === 'unsubscribe' ||
                item === 'deleteGroup' ||
                item === 'deleteChannel';
              return (
                <AppPressable
                  key={item}
                  style={styles.menuRow}
                  onPress={() => runMenuItem(menuChat, item)}
                  accessibilityRole="button"
                  accessibilityLabel={menuItemLabel(item)}>
                  {item === 'callAudio' ? (
                    <TintedIcon
                      source={require('../../assets/chat-icons/phone.png')}
                      style={styles.actionIconImage}
                      tintColor={chatListHeaderIconTint}
                    />
                  ) : item === 'callVideo' ? (
                    <VideoCameraGlyph color={chatListHeaderIconTint} />
                  ) : item === 'mute' ? (
                    <MutedBellGlyph color={chatListHeaderIconTint} size={20} />
                  ) : item === 'unmute' ? (
                    <BellGlyph color={chatListHeaderIconTint} />
                  ) : null}
                  <Text style={[styles.menuRowText, danger && styles.menuRowDangerText]}>{menuItemLabel(item)}</Text>
                  {item === 'mute' ? <Text style={styles.menuRowCheck}>›</Text> : null}
                </AppPressable>
              );
            })
          : null}
        {menuChat && menuPage === 'mute' ? (
          <>
            {(['1h', '8h', 'forever'] as const).map((duration) => (
              <AppPressable
                key={duration}
                style={styles.menuRow}
                onPress={() => {
                  const chat = menuChat;
                  closeRowMenu();
                  applyMute(chat, duration);
                }}
                accessibilityRole="button">
                <Text style={styles.menuRowText}>
                  {duration === '1h' ? t('chatListMute1h') : duration === '8h' ? t('chatListMute8h') : t('chatListMuteForever')}
                </Text>
              </AppPressable>
            ))}
            {/* Заглушение глушит сообщения, но не звонки: прежняя подсказка обещала обратное. */}
            <Text style={styles.menuHint}>{t('chatListMuteCallsHint')}</Text>
          </>
        ) : null}
        {menuChat && menuPage === 'deleteDirect' ? (
          <>
            <Text style={styles.modalTitle}>{fillTemplate(t('chatListDeleteForMeTitle'), { name: menuChat.title })}</Text>
            <Text style={styles.hint}>{fillTemplate(t('chatListDeleteForMeBody'), { name: menuChat.title })}</Text>
            <AppPressable
              style={styles.checkRow}
              onPress={() => setDeleteForPeerToo((v) => !v)}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: deleteForPeerToo }}>
              <View style={[styles.checkBox, deleteForPeerToo && styles.checkBoxOn]}>
                {deleteForPeerToo ? <CheckGlyph color="#fff" size={14} /> : null}
              </View>
              <Text style={styles.checkRowText}>{fillTemplate(t('chatListDeleteAlsoFor'), { name: menuChat.title })}</Text>
            </AppPressable>
            {deleteForPeerToo ? <Text style={styles.menuHint}>{t('chatListDeleteAlsoForHint')}</Text> : null}
            <View style={styles.modalRow}>
              <AppPressable style={styles.modalBtn} onPress={closeRowMenu} disabled={menuBusy}>
                <Text style={styles.modalBtnText}>{t('cancel')}</Text>
              </AppPressable>
              <AppPressable
                style={[styles.modalBtn, styles.modalDanger]}
                onPress={() => void performDeleteDirect(menuChat, deleteForPeerToo)}
                disabled={menuBusy}
                accessibilityRole="button"
                accessibilityLabel={t('chatListDeleteConfirm')}>
                {menuBusy ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('chatListDeleteConfirm')}</Text>
                )}
              </AppPressable>
            </View>
          </>
        ) : null}
        {menuChat && menuPage === 'confirm' && menuConfirm ? (
          <>
            <Text style={styles.hint}>
              {menuConfirm === 'unsubscribe'
                ? t('channelUnsubscribeConfirmBody')
                : menuConfirm === 'leaveGroup'
                  ? t('groupLeaveBody')
                  : menuConfirm === 'deleteGroup'
                    ? t('chatListDeleteGroupBody')
                    : t('deleteChannelConfirmBody')}
            </Text>
            <View style={styles.modalRow}>
              <AppPressable style={styles.modalBtn} onPress={closeRowMenu} disabled={menuBusy}>
                <Text style={styles.modalBtnText}>{t('cancel')}</Text>
              </AppPressable>
              <AppPressable
                style={[styles.modalBtn, styles.modalDanger]}
                onPress={() => void performConfirmed(menuChat, menuConfirm)}
                disabled={menuBusy}
                accessibilityRole="button">
                {menuBusy ? (
                  <ActivityIndicator color="#fff" />
                ) : (
                  <Text style={[styles.modalBtnText, { color: '#fff' }]}>
                    {menuConfirm === 'unsubscribe'
                      ? t('channelUnsubscribe')
                      : menuConfirm === 'leaveGroup'
                        ? t('groupLeaveConfirm')
                        : t('chatListDeleteConfirm')}
                  </Text>
                )}
              </AppPressable>
            </View>
          </>
        ) : null}
      </BottomSheet>

      <BottomSheet
        visible={channelsHubOpen}
        onClose={closeChannelsHub}
        palette={sheetPalette}>
        <Text style={styles.modalTitle}>{t('channelsHubTitle')}</Text>
        <TextInput
          style={styles.input}
          value={channelHubQuery}
          onChangeText={setChannelHubQuery}
          maxLength={SEARCH_QUERY_MAX_CHARS}
          placeholder={t('channelSearchPlaceholder')}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {channelDirectoryLoading ? <ActivityIndicator color={chatListHeaderIconTint} style={{ marginVertical: 8 }} /> : null}
        <FlatList
          style={styles.dmList}
          data={channelDirectory}
          keyExtractor={(item) => item.id}
          // Без этого первый тап по каналу при открытой клавиатуре только её прячет.
          keyboardShouldPersistTaps="handled"
          ListEmptyComponent={!channelDirectoryLoading ? <Text style={styles.dmEmpty}>{t('channelsHubEmpty')}</Text> : null}
          renderItem={({ item }) => (
            <View style={styles.channelDirCard}>
              <View style={styles.channelDirCardTop}>
                <AvatarBadge
                  chatAvatarChatId={item.id}
                  displayName={item.title}
                  hasAvatar={item.chatHasAvatar}
                  revision={item.chatAvatarRevision ?? 0}
                  shapeId="circle"
                  size={40}
                />
                <AppPressable
                  style={{ flex: 1, minWidth: 0 }}
                  onPress={() => {
                    // Не подписан — показываем предпросмотр вместо глухого «ничего не происходит».
                    if (!item.subscribed) {
                      void openChannelPreview(item);
                      return;
                    }
                    navigation.navigate('Chat', {
                      chatId: item.id,
                      title: item.title,
                      isGroup: false,
                      isChannel: true,
                      canPost: item.myRole === 'ADMIN' || item.myRole === 'EDITOR',
                      canComment: item.allowSubscriberComments,
                      canReact: item.allowSubscriberReactions,
                      canManage: item.canManage,
                      myRole: item.myRole ?? null,
                      channelDescription: item.description ?? null,
                      memberCount: item.memberCount,
                      chatHasAvatar: Boolean(item.chatHasAvatar),
                      chatAvatarRevision:
                        typeof item.chatAvatarRevision === 'number' && Number.isFinite(item.chatAvatarRevision)
                          ? item.chatAvatarRevision
                          : 0,
                      chatAvatarShape: item.chatAvatarShape ?? null,
                    });
                    setChannelsHubOpen(false);
                  }}>
                  <Text style={styles.dmRowName}>{item.title}</Text>
                  {item.description ? <Text style={styles.channelDirDescription}>{item.description}</Text> : null}
                  <Text style={styles.channelDirMeta}>
                    {t('channelSubscribersCount').replace('{count}', String(item.memberCount))}
                  </Text>
                </AppPressable>
              </View>
              <View style={styles.channelDirActions}>
                <AppPressable
                  style={[styles.modalBtn, styles.channelActionBtn]}
                  onPress={() => void toggleChannelSubscription(item)}>
                  <Text style={styles.modalBtnText}>
                    {item.subscribed ? t('channelUnsubscribe') : t('channelSubscribe')}
                  </Text>
                </AppPressable>
                {item.canManage ? (
                  <AppPressable
                    style={[styles.modalBtn, styles.channelActionBtn]}
                    onPress={() => void openManageChannel(item)}>
                    <Text style={styles.modalBtnText}>{t('channelManage')}</Text>
                  </AppPressable>
                ) : null}
                {item.slug ? (
                  <AppPressable
                    style={[styles.modalBtn, styles.channelActionBtn]}
                    onPress={() => {
                      const url = buildChannelUrl(item.slug!);
                      track('channel_shared', { target: 'channel' });
                      void Share.share({ message: url, url });
                    }}>
                    <Text style={styles.modalBtnText}>{t('shareChannel')}</Text>
                  </AppPressable>
                ) : null}
              </View>
            </View>
          )}
        />
        <View style={styles.modalRow}>
          <AppPressable style={styles.modalBtn} onPress={closeChannelsHub}>
            <Text style={styles.modalBtnText}>{t('close')}</Text>
          </AppPressable>
          <AppPressable
            style={[styles.modalBtn, styles.modalPrimary]}
            onPress={() => {
              closeChannelsHub();
              navigation.navigate('NewChannel');
            }}>
            <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('newChannelTitle')}</Text>
          </AppPressable>
        </View>
      </BottomSheet>
      <BottomSheet
        visible={previewChannel !== null}
        onClose={() => closeChannelPreview({ returnToHub: true })}
        palette={sheetPalette}>
        <Text style={styles.modalTitle}>{t('channelPreviewTitle')}</Text>
        {previewChannel ? (
          <View style={styles.channelDirRow}>
            <AvatarBadge
              chatAvatarChatId={previewChannel.id}
              displayName={previewChannel.title}
              hasAvatar={previewChannel.chatHasAvatar}
              revision={previewChannel.chatAvatarRevision ?? 0}
              shapeId="circle"
              size={40}
            />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.dmRowName}>{previewChannel.title}</Text>
              <Text style={styles.channelDirMeta}>
                {t('channelSubscribersCount').replace(
                  '{count}',
                  String(previewData?.subscriberCount ?? previewChannel.memberCount),
                )}
              </Text>
            </View>
          </View>
        ) : null}
        {previewChannel?.description ? (
          <Text style={styles.channelDirDescription}>{previewChannel.description}</Text>
        ) : null}
        {previewLoading ? (
          <ActivityIndicator color={chatListHeaderIconTint} style={{ marginVertical: 10 }} />
        ) : null}
        {previewError ? <Text style={styles.dmErr}>{previewError}</Text> : null}
        {previewData ? (
          <ScrollView
            style={{ flexShrink: 1, marginTop: 8 }}
            contentContainerStyle={{ gap: 10, paddingBottom: 4 }}
            showsVerticalScrollIndicator={false}>
            {previewData.recentPosts.length === 0 ? (
              <Text style={styles.dmEmpty}>{t('channelPreviewEmptyPosts')}</Text>
            ) : (
              /*
                Карточки нажимаются и показывают обложку. Раньше витрина была списком заголовков,
                по которым нельзя было нажать: человек решал, подписываться ли, по трём строкам
                текста — то есть вслепую. Теперь статью можно прочитать до подписки, и это
                недостающий шаг воронки, а не украшение.
              */
              previewData.recentPosts.map((post) => (
                <AppPressable
                  key={post.slug}
                  style={styles.channelPreviewCard}
                  onPress={() => {
                    const ch = previewChannel;
                    closeChannelPreview({ returnToHub: false });
                    navigation.navigate('ChannelPost', {
                      channelTitle: ch?.title ?? previewData.title,
                      publicHandle: previewData.handle,
                      publicSlug: post.slug,
                    });
                  }}>
                  {post.coverUrl ? (
                    <Image
                      source={{ uri: post.coverUrl }}
                      style={styles.channelPreviewCover}
                      resizeMode="cover"
                    />
                  ) : null}
                  <Text style={styles.dmRowName}>{post.title}</Text>
                  {post.previewText ? (
                    <Text style={styles.channelDirDescription} numberOfLines={3}>
                      {post.previewText}
                    </Text>
                  ) : null}
                </AppPressable>
              ))
            )}
          </ScrollView>
        ) : null}
        {previewChannel && !previewChannel.subscribed ? (
          <Text style={styles.settingsInlineHint}>{t('channelPreviewSubscribeHint')}</Text>
        ) : null}
        <View style={styles.modalRow}>
          <AppPressable style={styles.modalBtn} onPress={() => closeChannelPreview({ returnToHub: true })}>
            <Text style={styles.modalBtnText}>{t('close')}</Text>
          </AppPressable>
          {previewChannel && !previewChannel.subscribed ? (
            <AppPressable
              style={[styles.modalBtn, styles.modalPrimary]}
              onPress={() => previewChannel && void subscribeFromPreview(previewChannel)}>
              <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('channelSubscribe')}</Text>
            </AppPressable>
          ) : null}
        </View>
      </BottomSheet>
      <BottomSheet
        visible={channelManageOpen}
        onClose={closeManageChannel}
        palette={sheetPalette}>
        <Text style={styles.modalTitle}>{t('channelManage')}</Text>
        <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ gap: 10, paddingBottom: 4 }} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          {manageChannel ? (
            <ChatAvatarEditor
              chatId={manageChannel.id}
              displayName={manageName.trim() || manageChannel.title}
              initialHasAvatar={Boolean(manageChannel.chatHasAvatar)}
              initialRevision={
                typeof manageChannel.chatAvatarRevision === 'number' &&
                Number.isFinite(manageChannel.chatAvatarRevision)
                  ? manageChannel.chatAvatarRevision
                  : 0
              }
              initialShape={manageChannel.chatAvatarShape ?? null}
              canEdit={manageChannel.myRole === 'ADMIN' || manageChannel.myRole === 'EDITOR'}
              onApplied={(next) => {
                const id = manageChannel.id;
                setManageChannel((prev) => (prev && prev.id === id ? { ...prev, ...next } : prev));
                setChannelDirectory((rows) => rows.map((r) => (r.id === id ? { ...r, ...next } : r)));
                setItems((rows) => rows.map((r) => (r.id === id ? { ...r, ...next } : r)));
              }}
            />
          ) : null}
          <View style={{ height: 1, backgroundColor: 'rgba(128,128,128,0.2)', marginVertical: 4 }} />
          <TextInput
            value={manageName}
            onChangeText={setManageName}
            maxLength={CHAT_TITLE_MAX_CHARS}
            style={styles.input}
            placeholder={t('channelNamePlaceholder')}
          />
          <TextInput
            value={manageDescription}
            onChangeText={setManageDescription}
            maxLength={CHAT_DESCRIPTION_MAX_CHARS}
            style={styles.input}
            placeholder={t('channelDescriptionPlaceholder')}
            multiline
          />
          <Text style={styles.hint}>{t('channelSlugLabel')}</Text>
          <TextInput
            value={manageSlug}
            onChangeText={(next) => {
              setManageSlug(next);
              setManageSlugError(null);
            }}
            maxLength={32}
            style={styles.input}
            placeholder={t('channelSlugPlaceholder')}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <Text style={styles.hint}>{t('channelSlugHint')}</Text>
          {manageSlugError ? <Text style={styles.dmErr}>{manageSlugError}</Text> : null}
          <View style={styles.channelPermsRow}>
            <AppPressable
              onPress={() => setManageAllowReactions((prev) => !prev)}
              hitSlop={6}
              style={[styles.druzhinaFilterChip, manageAllowReactions && styles.druzhinaFilterChipActive]}>
              <Text style={[styles.druzhinaFilterChipText, manageAllowReactions && styles.druzhinaFilterChipTextActive]}>
                {t('channelAllowReactions')}
              </Text>
            </AppPressable>
            <AppPressable
              onPress={() => setManageAllowComments((prev) => !prev)}
              hitSlop={6}
              style={[styles.druzhinaFilterChip, manageAllowComments && styles.druzhinaFilterChipActive]}>
              <Text style={[styles.druzhinaFilterChipText, manageAllowComments && styles.druzhinaFilterChipTextActive]}>
                {t('channelAllowComments')}
              </Text>
            </AppPressable>
          </View>
          <Text style={styles.hint}>{t('channelEditorsHint')}</Text>
          {manageEditors.map((editor) => (
            <View key={editor.id} style={styles.gSelectedRow}>
              <Text style={styles.gSelectedName}>{editor.displayName}</Text>
              <AppPressable onPress={() => void removeEditorFromManagedChannel(editor)} hitSlop={8}>
                <Text style={styles.gRemove}>{t('removeMember')}</Text>
              </AppPressable>
            </View>
          ))}
          <TextInput
            placeholder={t('nicknamePlaceholder')}
            value={manageEditorQuery}
            onChangeText={changeManageEditorQuery}
            maxLength={SEARCH_QUERY_MAX_CHARS}
            style={styles.input}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {manageEditorBusy ? <ActivityIndicator color={chatListHeaderIconTint} style={{ marginVertical: 8 }} /> : null}
          {manageEditorSearch.error ? (
            <InlineRetryError
              text={humanizeError(manageEditorSearch.error, t('searchError'))}
              retryLabel={t('actionRetry')}
              onRetry={manageEditorSearch.retry}
              style={{ marginVertical: 8 }}
              textStyle={styles.dmErr}
              buttonStyle={styles.pillSecondary}
              buttonTextStyle={styles.pillSecondaryText}
            />
          ) : null}
          {manageEditorResults
            .filter((r) => !manageEditors.some((e) => e.id === r.id) && r.id !== user?.userId)
            .map((item) => (
              <AppPressable key={item.id} style={styles.dmRow} onPress={() => void addEditorToManagedChannel(item)}>
                <Text style={styles.dmRowName}>{item.displayName}</Text>
                <Text style={styles.dmRowHint}>{t('addEditor')}</Text>
              </AppPressable>
            ))}
        </ScrollView>
        <View style={styles.modalRow}>
          <AppPressable style={styles.modalBtn} onPress={closeManageChannel}>
            <Text style={styles.modalBtnText}>{t('cancel')}</Text>
          </AppPressable>
          {manageChannel ? (
            <AppPressable
              style={[styles.modalBtn, styles.modalDanger]}
              onPress={() => {
                const ch = manageChannel;
                void confirmAction({
                  title: t('deleteChatConfirmTitle'),
                  body: t('deleteChannelConfirmBody'),
                  confirmText: t('deleteChannel'),
                  cancelText: t('cancel'),
                  destructive: true,
                }).then((ok) => {
                  if (!ok) return;
                  void performDeleteChannel(ch.id);
                  closeManageChannel();
                  void loadChannelsDirectory();
                });
              }}>
              <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('deleteChannel')}</Text>
            </AppPressable>
          ) : null}
          <AppPressable style={[styles.modalBtn, styles.modalPrimary]} onPress={() => void saveManageChannel()}>
            <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('saveChannel')}</Text>
          </AppPressable>
        </View>
      </BottomSheet>

      <SelfStatusSheet
        visible={selfStatusOpen}
        onClose={() => setSelfStatusOpen(false)}
        styles={styles}
        sheetPalette={sheetPalette}
        themeChipPalette={themeChipPalette}
        statusText={user?.statusText}
        onSave={saveSelfStatus}
        doNotDisturb={user?.doNotDisturb === true}
        onToggleDoNotDisturb={toggleDoNotDisturb}
      />
      <SettingsModal visible={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <HelpCenterModal
        visible={helpOpen}
        onClose={() => setHelpOpen(false)}
        entries={helpCenter.entries}
        unseenIds={helpUnseenIds}
        onMarkAllRead={helpCenter.markAllRead}
        palette={helpPalette}
      />

      {/* Выбор чата, куда уходит карточка контакта (стори 5.11). */}
      <BottomSheet
        visible={shareContactTarget != null}
        onClose={() => setShareContactTarget(null)}
        palette={sheetPalette}>
        <Text style={styles.modalTitle}>{t('contactShareChooseChat')}</Text>
        {shareContactTarget ? (
          <Text style={styles.hint} numberOfLines={1}>
            {shareContactTarget.displayName}
          </Text>
        ) : null}
        <FlatList
          style={styles.dmList}
          data={items}
          keyExtractor={(c) => c.id}
          ListEmptyComponent={<Text style={styles.dmEmpty}>{t('nobodyFound')}</Text>}
          renderItem={({ item }) => (
            <AppPressable
              style={styles.dmRow}
              onPress={() => void sendContactCardToChat(item)}
              accessibilityRole="button">
              <Text style={styles.dmRowName} numberOfLines={1}>
                {item.title}
              </Text>
            </AppPressable>
          )}
        />
        <View style={styles.modalRow}>
          <AppPressable style={styles.modalBtn} onPress={() => setShareContactTarget(null)}>
            <Text style={styles.modalBtnText}>{t('cancel')}</Text>
          </AppPressable>
        </View>
      </BottomSheet>

      <BottomSheet
        visible={bossOpen}
        onClose={closeBossModal}
        palette={sheetPalette}
        overlay={
          <>
          {/*
            Подтверждение — слой внутри той же модалки: второй <Modal> поверх
            открытого на Android показывается под ним, и удаление выглядело как
            «кнопка ничего не делает».
          */}
          {userToDelete != null ? (
            <View style={[styles.modalBg, styles.bossConfirmOverlay]}>
              <View style={styles.modalCard}>
                <Text style={styles.modalTitle}>{t('bossDeleteConfirmTitle')}</Text>
                <Text style={styles.hint}>{t('bossDeleteConfirmBody')}</Text>
                <View style={styles.modalRow}>
                  <AppPressable style={styles.modalBtn} onPress={() => setUserToDelete(null)}>
                    <Text style={styles.modalBtnText}>{t('cancel')}</Text>
                  </AppPressable>
                  <AppPressable
                    style={[styles.modalBtn, styles.modalDanger]}
                    onPress={() => {
                      const row = userToDelete;
                      setUserToDelete(null);
                      if (row) {
                        void performDeleteBossUser(row);
                      }
                    }}>
                    <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('bossDeleteUser')}</Text>
                  </AppPressable>
                </View>
              </View>
            </View>
          ) : null}
          <UserAvatarPreviewModal
            presentation="overlay"
            visible={bossUserCard != null}
            onClose={() => setBossUserCard(null)}
            target={bossUserCard}
          />
          </>
        }>
        <ScrollView
          style={styles.bossCardScroll}
          contentContainerStyle={{ gap: SPACE.md }}
          keyboardShouldPersistTaps="handled">
        <Text style={styles.modalTitle}>{t('bossUsersTitle')}</Text>
        <Text style={styles.hint}>{t('bossInstructionLanguage')}</Text>
        <LanguagePickerRow
          locale={bossInstructionLocale}
          onSelect={setBossInstructionLocale}
          palette={{
            chipBg: listPal.modalBtn,
            chipBorder: listPal.smallBtnBorder,
            chipSelectedBg: listPal.chatFilterChipActive,
            chipSelectedBorder: listPal.chatFilterChipActiveBorder,
          }}
        />
        <TextInput
          placeholder={t('bossUserLoginPlaceholder')}
          value={bossNewLogin}
          onChangeText={setBossNewLogin}
          maxLength={LOGIN_MAX_CHARS}
          style={styles.input}
          autoCapitalize="none"
          autoCorrect={false}
        />
        <TextInput
          placeholder={t('bossDisplayNamePlaceholder')}
          value={bossNewName}
          onChangeText={setBossNewName}
          maxLength={DISPLAY_NAME_MAX_CHARS}
          style={styles.input}
        />
        <Text style={styles.hint}>{t('bossTempPasswordHint')}</Text>
        <View style={styles.bossPasswordRow}>
          <TextInput
            placeholder={t('bossNewUserPassword')}
            value={bossNewPassword}
            onChangeText={setBossNewPassword}
            maxLength={PASSWORD_MAX_CHARS}
            style={[styles.input, styles.bossPasswordInput]}
            autoCapitalize="none"
            autoCorrect={false}
          />
          <AppPressable
            style={styles.bossRegenBtn}
            onPress={() => setBossNewPassword(generateTemporaryPassword())}
            hitSlop={6}
            accessibilityRole="button"
            accessibilityLabel={t('bossRegeneratePassword')}>
            <Text style={styles.bossRegenBtnText}>{t('bossRegeneratePassword')}</Text>
          </AppPressable>
        </View>
        {bossError ? <Text style={styles.dmErr}>{bossError}</Text> : null}
        <AppPressable
          style={[styles.modalBtn, styles.modalPrimary, { alignSelf: 'flex-start' }]}
          onPress={() => void submitBossCreate()}
          disabled={bossBusy}>
          <Text style={[styles.modalBtnText, { color: '#fff' }]}>{t('bossAddUser')}</Text>
        </AppPressable>
        {bossLastCredentials ? (
          <View style={styles.bossCopyBlock}>
            <Text style={styles.hint}>{t('bossCredentialsSentHint')}</Text>
            <AppPressable
              style={[styles.modalBtn, styles.bossCopyBtn]}
              onPress={() => {
                const text = buildBossInviteCopyText(
                  bossLastCredentials.instructionLocale,
                  bossLastCredentials.login,
                  bossLastCredentials.password,
                  bossRetentionDays,
                );
                void Clipboard.setString(text);
                showToast({ message: t('bossCopyInviteDone') });
              }}
              accessibilityRole="button"
              accessibilityLabel={t('bossCopyInviteButton')}>
              <Text style={styles.modalBtnText}>{t('bossCopyInviteButton')}</Text>
            </AppPressable>
          </View>
        ) : null}
        {bossBusy ? <ActivityIndicator color={chatListHeaderIconTint} style={{ marginVertical: 8 }} /> : null}
        {/*
          Список рисуется через map, а не FlatList: он живёт внутри ScrollView
          (вложенный VirtualizedList той же оси не скроллится и ругается в консоль),
          а пользователей у BOSS — десятки, не тысячи.
        */}
        <View style={styles.bossList}>
          {bossUsers.length === 0 && !bossBusy ? (
            <Text style={styles.dmEmpty}>{t('nobodyFound')}</Text>
          ) : null}
          {/*
            Строка — View, а не Pressable: вложенный Pressable внутри
            Pressable на Android отдаёт касание внешнему, и «Удалить»
            молчало. Теперь это две независимые кнопки-соседа.
          */}
          {bossUsers.map((item) => (
            <View key={item.id} style={styles.bossRow}>
              <AppPressable
                style={styles.bossRowOpen}
                onPress={() => openBossUserCard(item)}
                accessibilityRole="button"
                accessibilityLabel={t('userCardTitle')}>
                <Text style={styles.bossRowTitle}>{item.displayName}</Text>
                <Text style={styles.bossRowMeta}>{item.login}</Text>
              </AppPressable>
              {item.appRole !== 'BOSS' ? (
                <AppPressable
                  onPress={() => setUserToDelete(item)}
                  hitSlop={8}
                  style={styles.bossDeleteBtn}
                  accessibilityRole="button"
                  accessibilityLabel={t('bossDeleteUser')}>
                  <Text style={styles.bossDelete}>{t('bossDeleteUser')}</Text>
                </AppPressable>
              ) : null}
            </View>
          ))}
        </View>
        <View style={styles.modalRow}>
          <AppPressable style={styles.modalBtn} onPress={() => void loadBossUsers()}>
            <Text style={styles.modalBtnText}>{t('groupRetry')}</Text>
          </AppPressable>
          <AppPressable style={styles.modalBtn} onPress={closeBossModal}>
            <Text style={styles.modalBtnText}>{t('close')}</Text>
          </AppPressable>
        </View>
        </ScrollView>
      </BottomSheet>
      </View>
    </View>
  );
}
